<template>
  <div class="taxi-driver-info">
    <!-- Вкладки для переключения между поездками, транзакциями и аналитикой -->
    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>
        <v-icon left>mdi-taxi</v-icon>
        Поездки
      </v-tab>
      <v-tab>
        <v-icon left>mdi-credit-card-multiple</v-icon>
        Транзакции
      </v-tab>
      <v-tab>
        <v-icon left>mdi-chart-line</v-icon>
        Аналитика
      </v-tab>
      <v-tab>
        <v-icon left>mdi-shield-car</v-icon>
        Безопасность
      </v-tab>
      <v-tab>
        <v-icon left>mdi-cog</v-icon>
        Управление
      </v-tab>
    </v-tabs>

    <!-- Заголовок с фильтрами -->
    <v-card class="mb-4">
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">{{ getTabIcon() }}</v-icon>
        {{ getTabTitle() }}
        <v-spacer></v-spacer>
        <v-btn v-if="activeTab === 0" small color="primary" @click="loadOrders()" :loading="isLoading"
          :disabled="!client?.yandex_driver_id">
          <v-icon left small>mdi-refresh</v-icon>
          Обновить
        </v-btn>
        <v-btn v-else-if="activeTab === 1" small color="primary" @click="loadTransactions()"
          :loading="isLoadingTransactions" :disabled="!client?.yandex_driver_id">
          <v-icon left small>mdi-refresh</v-icon>
          Обновить
        </v-btn>
        <v-btn v-else-if="activeTab === 2" small color="primary" @click="loadAnalytics()" :loading="isLoadingAnalytics"
          :disabled="!client?.yandex_driver_id">
          <v-icon left small>mdi-refresh</v-icon>
          Обновить аналитику
        </v-btn>
        <v-btn v-else-if="activeTab === 3" small color="primary" @click="loadSafetyAnalytics()"
          :loading="isLoadingAnalytics" :disabled="!client?.yandex_driver_id">
          <v-icon left small>mdi-refresh</v-icon>
          Обновить данные
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <!-- Период -->
          <v-col cols="12" md="3" v-if="activeTab !== 4">
            <v-select v-model="selectedPeriod" :items="periodOptions" label="Период" @change="onPeriodChange">
            </v-select>
          </v-col>

          <!-- Кастомный период -->
          <v-col cols="12" md="5" v-if="selectedPeriod === 'custom' && activeTab !== 4">
            <div class="d-flex align-center">
              <v-text-field v-model="customStartDate" type="date" label="С даты" class="mr-2" :max="customEndDate"
                @change="onCustomDateChange">
              </v-text-field>
              <v-text-field v-model="customEndDate" type="date" label="По дату" :min="customStartDate" :max="today"
                @change="onCustomDateChange">
              </v-text-field>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Таблица поездок -->
    <v-card v-if="client?.yandex_driver_id && activeTab === 0">
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
        Список поездок
        <v-spacer></v-spacer>
        <v-chip small :color="orders.length > 0 ? 'success' : 'grey'">
          {{ orders.length }}{{ hasMoreOrders ? '+' : '' }} поездок
        </v-chip>
      </v-card-title>

      <v-data-table :headers="headers" :items="orders" :loading="isLoading" :items-per-page="20" class="elevation-1"
        :no-data-text="isLoading ? 'Загрузка...' : 'Поездки не найдены'" :loading-text="'Загрузка поездок...'">

        <!-- ID заказа -->
        <template v-slot:item.id="{ item }">
          <v-chip small color="primary" text-color="white">
            {{ item.short_id }}
          </v-chip>
        </template>

        <!-- Статус -->
        <template v-slot:item.status="{ item }">
          <v-chip small :color="getStatusColor(item.status)" text-color="white">
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Время создания -->
        <template v-slot:item.created_at="{ item }">
          {{ formatDateTime(item.created_at) }}
        </template>

        <!-- Время завершения -->
        <template v-slot:item.ended_at="{ item }">
          {{ item.ended_at ? formatDateTime(item.ended_at) : '-' }}
        </template>

        <!-- Стоимость -->
        <template v-slot:item.cost="{ item }">
          <span class="font-weight-bold" :class="item.cost > 0 ? 'success--text' : 'grey--text'">
            {{ formatPrice(item.cost) }}
          </span>
        </template>

        <!-- Категория -->
        <template v-slot:item.category="{ item }">
          <v-chip small :color="getCategoryColor(item.category)" text-color="white">
            {{ getCategoryText(item.category) }}
          </v-chip>
        </template>

        <!-- Откуда -->
        <template v-slot:item.route_from="{ item }">
          <div class="text-truncate" style="max-width: 200px;" :title="item.route_from">
            {{ item.route_from }}
          </div>
        </template>

        <!-- Куда -->
        <template v-slot:item.route_to="{ item }">
          <div class="text-truncate" style="max-width: 200px;" :title="item.route_to">
            {{ item.route_to }}
          </div>
        </template>

        <!-- Автомобиль -->
        <template v-slot:item.car_info="{ item }">
          <div class="text-truncate" style="max-width: 150px;" :title="item.car_info">
            {{ item.car_info }}
          </div>
        </template>

        <!-- Действия -->
        <template v-slot:item.actions="{ item }">
          <v-btn small icon color="primary" @click="showOrderDetails(item)" :disabled="!item.id">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Кнопка "Загрузить еще" для поездок -->
      <v-card-actions v-if="hasMoreOrders">
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="isLoadingMoreOrders" @click="loadMoreOrders">
          <v-icon left>mdi-chevron-down</v-icon>
          Загрузить еще
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>

    <!-- Таблица транзакций -->
    <v-card v-if="client?.yandex_driver_id && activeTab === 1">
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
        Список транзакций
        <v-spacer></v-spacer>
        <v-chip small :color="allTransactions.length > 0 ? 'success' : 'grey'">
          {{ allTransactions.length }}{{ hasMoreTransactions ? '+' : '' }} транзакций
        </v-chip>
      </v-card-title>

      <v-data-table :headers="allTransactionsHeaders" :items="allTransactions" :loading="isLoadingTransactions"
        :items-per-page="20" class="elevation-1"
        :no-data-text="isLoadingTransactions ? 'Загрузка...' : 'Транзакции не найдены'"
        :loading-text="'Загрузка транзакций...'">

        <!-- ID транзакции -->
        <template v-slot:item.id="{ item }">
          <v-chip small color="info" text-color="white">
            {{ item.id }}
          </v-chip>
        </template>

        <!-- Дата -->
        <template v-slot:item.event_at="{ item }">
          {{ formatDateTime(item.event_at) }}
        </template>

        <!-- Сумма -->
        <template v-slot:item.amount="{ item }">
          <span class="font-weight-bold" :class="parseFloat(item.amount) > 0 ? 'success--text' : 'error--text'">
            {{ parseFloat(item.amount) > 0 ? '+' : '' }}{{ formatPrice(item.amount) }}
          </span>
        </template>

        <!-- Категория -->
        <template v-slot:item.category_name="{ item }">
          <v-chip small color="grey" text-color="white">
            {{ item.category_name }}
          </v-chip>
        </template>

        <!-- Описание -->
        <template v-slot:item.description="{ item }">
          <div class="text-truncate" style="max-width: 250px;" :title="item.description">
            {{ item.description }}
          </div>
        </template>

        <!-- Создатель -->
        <template v-slot:item.created_by="{ item }">
          <div class="text-truncate" style="max-width: 150px;" :title="item.created_by">
            {{ item.created_by }}
          </div>
        </template>

        <!-- Валюта -->
        <template v-slot:item.currency_code="{ item }">
          {{ item.currency_code }}
        </template>
      </v-data-table>

      <!-- Кнопка "Загрузить еще" для транзакций -->
      <v-card-actions v-if="hasMoreTransactions">
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="isLoadingMoreTransactions" @click="loadMoreTransactions">
          <v-icon left>mdi-chevron-down</v-icon>
          Загрузить еще
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>

    <!-- Вкладка аналитики -->
    <div v-if="client?.yandex_driver_id && activeTab === 2">
      <!-- Индикатор загрузки данных -->
      <v-alert v-if="analyticsLoadingProgress.isLoading" type="info" border="left" colored-border class="mb-4">
        <div class="d-flex align-center">
          <v-progress-circular indeterminate color="primary" class="mr-3"></v-progress-circular>
          <div class="flex-grow-1">
            <div class="font-weight-bold">Загрузка данных для аналитики...</div>
            <div class="text-caption">
              Поездок: {{ analyticsLoadingProgress.ordersLoaded }} ·
              Транзакций: {{ analyticsLoadingProgress.transactionsLoaded }}
            </div>
            <div class="text-caption" v-if="analyticsLoadingProgress.tracksTotal > 0">
              Треков: {{ analyticsLoadingProgress.tracksLoaded }} / {{ analyticsLoadingProgress.tracksTotal }}
            </div>
            <v-progress-linear v-if="analyticsLoadingProgress.tracksTotal > 0"
              :value="(analyticsLoadingProgress.tracksLoaded / analyticsLoadingProgress.tracksTotal) * 100"
              color="primary" height="4" class="mt-2"></v-progress-linear>
          </div>
        </div>
      </v-alert>

      <!-- Общая статистика -->
      <v-card class="mb-4" v-else>
        <v-card-title>
          <v-icon left color="primary">mdi-chart-box</v-icon>
          Общая статистика за период
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Количество поездок -->
            <v-col cols="12" md="3">
              <v-card :color="$vuetify.theme.dark ? 'blue darken-4' : 'blue lighten-4'" :dark="$vuetify.theme.dark"
                elevation="2" class="analytics-card">
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption mb-1"
                        :class="$vuetify.theme.dark ? 'blue--text lighten-3' : 'blue--text darken-2'">
                        Поездок
                      </div>
                      <div class="text-h3 font-weight-bold mb-1"
                        :class="$vuetify.theme.dark ? 'white--text' : 'blue--text darken-4'">
                        {{ formatNumber(analytics.totalOrders) }}
                      </div>
                      <div class="text-caption"
                        :class="$vuetify.theme.dark ? 'green--text lighten-2' : 'green--text darken-2'">
                        Завершено: {{ formatNumber(analytics.completedOrders || 0) }}
                      </div>
                    </div>
                    <v-icon size="56"
                      :color="$vuetify.theme.dark ? 'blue lighten-1' : 'blue darken-2'">mdi-taxi</v-icon>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Общий доход -->
            <v-col cols="12" md="3">
              <v-card :color="$vuetify.theme.dark ? 'green darken-4' : 'green lighten-4'" :dark="$vuetify.theme.dark"
                elevation="2" class="analytics-card">
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption mb-1"
                        :class="$vuetify.theme.dark ? 'green--text lighten-3' : 'green--text darken-2'">
                        Общий доход
                      </div>
                      <div class="text-h3 font-weight-bold mb-1"
                        :class="$vuetify.theme.dark ? 'white--text' : 'green--text darken-4'">
                        {{ formatPrice(analytics.totalRevenue) }}
                      </div>
                      <div class="text-caption"
                        :class="$vuetify.theme.dark ? 'grey--text lighten-2' : 'grey--text darken-2'">
                        Средняя: {{ formatPrice(analytics.avgOrderCost) }}
                      </div>
                    </div>
                    <v-icon size="56"
                      :color="$vuetify.theme.dark ? 'green lighten-1' : 'green darken-2'">mdi-cash-multiple</v-icon>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Баланс изменение -->
            <v-col cols="12" md="3">
              <v-card :color="$vuetify.theme.dark ? 'purple darken-4' : 'purple lighten-4'" :dark="$vuetify.theme.dark"
                elevation="2" class="analytics-card">
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption mb-1"
                        :class="$vuetify.theme.dark ? 'purple--text lighten-3' : 'purple--text darken-2'">
                        Баланс
                      </div>
                      <div class="text-h3 font-weight-bold mb-1" :class="analytics.balanceChange >= 0 ?
                        ($vuetify.theme.dark ? 'green--text lighten-2' : 'green--text darken-3') :
                        ($vuetify.theme.dark ? 'red--text lighten-2' : 'red--text darken-3')">
                        {{ analytics.balanceChange >= 0 ? '+' : '' }}{{ formatPrice(analytics.balanceChange) }}
                      </div>
                      <div class="text-caption"
                        :class="$vuetify.theme.dark ? 'grey--text lighten-2' : 'grey--text darken-2'">
                        Транзакций: {{ formatNumber(analytics.totalTransactions) }}
                      </div>
                    </div>
                    <v-icon size="56"
                      :color="$vuetify.theme.dark ? 'purple lighten-1' : 'purple darken-2'">mdi-wallet</v-icon>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Эффективность -->
            <v-col cols="12" md="3">
              <v-card :color="$vuetify.theme.dark ? 'orange darken-4' : 'orange lighten-4'" :dark="$vuetify.theme.dark"
                elevation="2" class="analytics-card">
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption mb-1"
                        :class="$vuetify.theme.dark ? 'orange--text lighten-3' : 'orange--text darken-2'">
                        Эффективность
                      </div>
                      <div class="text-h3 font-weight-bold mb-1"
                        :class="$vuetify.theme.dark ? 'white--text' : 'orange--text darken-4'">
                        {{ analytics.completionRate }}%
                      </div>
                      <div class="text-caption"
                        :class="$vuetify.theme.dark ? 'grey--text lighten-2' : 'grey--text darken-2'">
                        {{ analytics.avgOrdersPerDay }} поездок/день
                      </div>
                    </div>
                    <v-icon size="56"
                      :color="$vuetify.theme.dark ? 'orange lighten-1' : 'orange darken-2'">mdi-speedometer</v-icon>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Детальная финансовая аналитика -->
      <v-row>
        <v-col cols="12" md="6">
          <v-card>
            <v-card-title>
              <v-icon left color="success">mdi-trending-up</v-icon>
              Финансы
            </v-card-title>
            <v-card-text>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Пополнения баланса</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 success--text font-weight-bold">
                      +{{ formatPrice(analytics.totalDeposits) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Списания</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 error--text font-weight-bold">
                      -{{ formatPrice(analytics.totalWithdrawals) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="my-2"></v-divider>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Изменение баланса</v-list-item-title>
                    <v-list-item-subtitle class="text-h5 font-weight-bold"
                      :class="analytics.balanceChange >= 0 ? 'success--text' : 'error--text'">
                      {{ analytics.balanceChange >= 0 ? '+' : '' }}{{ formatPrice(analytics.balanceChange) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card>
            <v-card-title>
              <v-icon left color="info">mdi-chart-bar</v-icon>
              Показатели эффективности
            </v-card-title>
            <v-card-text>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Средний доход в день</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold">
                      {{ formatPrice(analytics.avgRevenuePerDay) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Средняя стоимость поездки</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold">
                      {{ formatPrice(analytics.avgOrderCost) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Процент завершенных поездок</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold">
                      {{ analytics.completionRate }}%
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Показатели безопасности и качества -->
      <v-row class="mt-4" v-if="analytics.safety.totalDistance > 0 || analytics.antifraud.avgRouteEfficiency > 0">
        <v-col cols="12" md="3">
          <v-card :color="$vuetify.theme.dark ? 'red darken-4' : 'red lighten-4'" :dark="$vuetify.theme.dark"
            elevation="2" class="analytics-card">
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption mb-1"
                    :class="$vuetify.theme.dark ? 'red--text lighten-3' : 'red--text darken-2'">
                    Превышения скорости
                  </div>
                  <div class="text-h3 font-weight-bold mb-1"
                    :class="$vuetify.theme.dark ? 'white--text' : 'red--text darken-4'">
                    {{ analytics.safety.speedViolations }}
                  </div>
                  <div class="text-caption"
                    :class="$vuetify.theme.dark ? 'grey--text lighten-2' : 'grey--text darken-2'">
                    За период
                  </div>
                </div>
                <v-icon size="56"
                  :color="$vuetify.theme.dark ? 'red lighten-1' : 'red darken-2'">mdi-speedometer-slow</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card :color="$vuetify.theme.dark ? 'cyan darken-4' : 'cyan lighten-4'" :dark="$vuetify.theme.dark"
            elevation="2" class="analytics-card">
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption mb-1"
                    :class="$vuetify.theme.dark ? 'cyan--text lighten-3' : 'cyan--text darken-2'">
                    Эффективность маршрутов
                  </div>
                  <div class="text-h3 font-weight-bold mb-1"
                    :class="$vuetify.theme.dark ? 'white--text' : 'cyan--text darken-4'">
                    {{ analytics.antifraud.avgRouteEfficiency }}%
                  </div>
                  <div class="text-caption"
                    :class="$vuetify.theme.dark ? 'grey--text lighten-2' : 'grey--text darken-2'">
                    Оптимальность пути
                  </div>
                </div>
                <v-icon size="56"
                  :color="$vuetify.theme.dark ? 'cyan lighten-1' : 'cyan darken-2'">mdi-map-marker-path</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card :color="$vuetify.theme.dark ? 'teal darken-4' : 'teal lighten-4'" :dark="$vuetify.theme.dark"
            elevation="2" class="analytics-card">
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption mb-1"
                    :class="$vuetify.theme.dark ? 'teal--text lighten-3' : 'teal--text darken-2'">
                    Общее расстояние
                  </div>
                  <div class="text-h3 font-weight-bold mb-1"
                    :class="$vuetify.theme.dark ? 'white--text' : 'teal--text darken-4'">
                    {{ formatDistance(analytics.safety.totalDistance) }}
                  </div>
                  <div class="text-caption"
                    :class="$vuetify.theme.dark ? 'grey--text lighten-2' : 'grey--text darken-2'">
                    За период
                  </div>
                </div>
                <v-icon size="56"
                  :color="$vuetify.theme.dark ? 'teal lighten-1' : 'teal darken-2'">mdi-map-marker-distance</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card :color="getDrivingScoreColor()" :dark="$vuetify.theme.dark" elevation="2" class="analytics-card">
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption mb-1"
                    :class="$vuetify.theme.dark ? 'yellow--text lighten-3' : 'yellow--text darken-3'">
                    Рейтинг вождения
                  </div>
                  <div class="text-h3 font-weight-bold mb-1"
                    :class="$vuetify.theme.dark ? 'white--text' : 'yellow--text darken-4'">
                    {{ analytics.safety.drivingScore }}/100
                  </div>
                  <div class="text-caption"
                    :class="$vuetify.theme.dark ? 'grey--text lighten-2' : 'grey--text darken-2'">
                    {{ getDrivingScoreLabel() }}
                  </div>
                </div>
                <v-icon size="56"
                  :color="$vuetify.theme.dark ? 'yellow lighten-1' : 'yellow darken-2'">mdi-shield-star</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Антифрод -->
      <v-card class="mt-4"
        v-if="analytics.antifraud && (analytics.antifraud.maxCost > 0 || analytics.antifraud.maxOrdersPerHour > 0)">
        <v-card-title>
          <v-icon left color="red">mdi-shield-alert</v-icon>
          Антифрод
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Максимальная стоимость поездки -->
            <v-col cols="12" md="3" v-if="analytics.antifraud.maxCost > 0">
              <v-alert :color="$vuetify.theme.dark ? 'red darken-2' : 'red lighten-4'" border="left" colored-border>
                <div class="text-caption">Максимальная стоимость</div>
                <div class="text-h6 font-weight-bold">{{ formatPrice(analytics.antifraud.maxCost) }}</div>
              </v-alert>
            </v-col>

            <!-- Максимальная продолжительность -->
            <v-col cols="12" md="3" v-if="analytics.antifraud.maxDuration > 0">
              <v-alert :color="$vuetify.theme.dark ? 'orange darken-2' : 'orange lighten-4'" border="left"
                colored-border>
                <div class="text-caption">Максимальная продолжительность</div>
                <div class="text-h6 font-weight-bold">{{ formatDuration(analytics.antifraud.maxDuration) }}</div>
              </v-alert>
            </v-col>

            <!-- Минимальная продолжительность -->
            <v-col cols="12" md="3" v-if="analytics.antifraud.minDuration > 0">
              <v-alert :color="$vuetify.theme.dark ? 'yellow darken-2' : 'yellow lighten-4'" border="left"
                colored-border>
                <div class="text-caption">Минимальная продолжительность</div>
                <div class="text-h6 font-weight-bold">{{ formatDuration(analytics.antifraud.minDuration) }}</div>
              </v-alert>
            </v-col>

            <!-- Максимальное количество поездок в час -->
            <v-col cols="12" md="3" v-if="analytics.antifraud.maxOrdersPerHour > 0">
              <v-alert :color="$vuetify.theme.dark ? 'purple darken-2' : 'purple lighten-4'" border="left"
                colored-border>
                <div class="text-caption">Максимум поездок в час</div>
                <div class="text-h6 font-weight-bold">{{ analytics.antifraud.maxOrdersPerHour }}</div>
              </v-alert>
            </v-col>

            <!-- Маршруты с отклонениями -->
            <v-col cols="12" md="3" v-if="analytics.antifraud.routeDeviations > 0">
              <v-alert :color="$vuetify.theme.dark ? 'deep-orange darken-2' : 'deep-orange lighten-4'" border="left"
                colored-border>
                <div class="text-caption">Подозрительные маршруты</div>
                <div class="text-h6 font-weight-bold">{{ analytics.antifraud.routeDeviations }}</div>
              </v-alert>
            </v-col>

            <!-- Подозрительные остановки -->
            <v-col cols="12" md="3" v-if="analytics.antifraud.suspiciousStops > 0">
              <v-alert :color="$vuetify.theme.dark ? 'brown darken-2' : 'brown lighten-4'" border="left" colored-border>
                <div class="text-caption">Подозрительные остановки</div>
                <div class="text-h6 font-weight-bold">{{ analytics.antifraud.suspiciousStops }}</div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Топ показатели -->
      <v-card class="mt-4" v-if="analytics.bestDay || analytics.maxOrderCost > 0">
        <v-card-title>
          <v-icon left color="warning">mdi-star</v-icon>
          Лучшие показатели
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4" v-if="analytics.bestDay">
              <v-alert :color="$vuetify.theme.dark ? 'blue darken-2' : 'blue lighten-4'" border="left" colored-border>
                <div class="text-caption">Лучший день</div>
                <div class="text-h6 font-weight-bold">{{ analytics.bestDay.date }}</div>
                <div class="text-body-2">{{ analytics.bestDay.orders }} поездок · {{
                  formatPrice(analytics.bestDay.revenue)
                  }}</div>
              </v-alert>
            </v-col>
            <v-col cols="12" md="4" v-if="analytics.maxOrderCost > 0">
              <v-alert :color="$vuetify.theme.dark ? 'green darken-2' : 'green lighten-4'" border="left" colored-border>
                <div class="text-caption">Самая дорогая поездка</div>
                <div class="text-h6 font-weight-bold">{{ formatPrice(analytics.maxOrderCost) }}</div>
              </v-alert>
            </v-col>
            <v-col cols="12" md="4" v-if="analytics.cancelledOrders > 0">
              <v-alert :color="$vuetify.theme.dark ? 'orange darken-2' : 'orange lighten-4'" border="left"
                colored-border>
                <div class="text-caption">Отмененные поездки</div>
                <div class="text-h6 font-weight-bold">{{ analytics.cancelledOrders }}</div>
                <div class="text-body-2">{{ ((analytics.cancelledOrders / analytics.totalOrders) * 100).toFixed(1) }}%
                  от
                  всех</div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- Вкладка безопасности водителя -->
    <div v-if="client?.yandex_driver_id && activeTab === 3">
      <!-- Индикатор загрузки данных -->
      <v-alert v-if="analyticsLoadingProgress.isLoading" type="info" border="left" colored-border class="mb-4">
        <div class="d-flex align-center">
          <v-progress-circular indeterminate color="primary" class="mr-3"></v-progress-circular>
          <div class="flex-grow-1">
            <div class="font-weight-bold">Загрузка данных для анализа безопасности...</div>
            <div class="text-caption">
              Поездок: {{ analyticsLoadingProgress.ordersLoaded }}
            </div>
            <div class="text-caption" v-if="analyticsLoadingProgress.tracksTotal > 0">
              Треков: {{ analyticsLoadingProgress.tracksLoaded }} / {{ analyticsLoadingProgress.tracksTotal }}
            </div>
            <v-progress-linear v-if="analyticsLoadingProgress.tracksTotal > 0"
              :value="(analyticsLoadingProgress.tracksLoaded / analyticsLoadingProgress.tracksTotal) * 100"
              color="primary" height="4" class="mt-2"></v-progress-linear>
          </div>
        </div>
      </v-alert>

      <!-- Общий рейтинг водителя -->
      <v-card class="mb-4" v-else>
        <v-card-title>
          <v-icon left color="primary">mdi-shield-star</v-icon>
          Общий рейтинг безопасности
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6" class="d-flex align-center justify-center">
              <div class="text-center">
                <v-progress-circular :value="analytics.safety.drivingScore" :size="200" :width="20"
                  :color="getDrivingScoreProgressColor()" class="mb-4">
                  <div class="text-h2 font-weight-bold">{{ analytics.safety.drivingScore }}</div>
                </v-progress-circular>
                <div class="text-h5 font-weight-bold">{{ getDrivingScoreLabel() }}</div>
                <div class="text-caption mt-2">из 100 возможных</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Превышения скорости</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold"
                      :class="analytics.safety.speedViolations > 0 ? 'error--text' : 'success--text'">
                      {{ analytics.safety.speedViolations }} случаев
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Средняя скорость</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold">
                      {{ analytics.safety.avgSpeed }} км/ч
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Общее расстояние</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold">
                      {{ formatDistance(analytics.safety.totalDistance) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Эффективность маршрутов</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold"
                      :class="analytics.antifraud.avgRouteEfficiency >= 80 ? 'success--text' : 'warning--text'">
                      {{ analytics.antifraud.avgRouteEfficiency }}%
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Детальные метрики безопасности -->
      <v-row>
        <v-col cols="12" md="6">
          <v-card>
            <v-card-title>
              <v-icon left color="error">mdi-alert-octagon</v-icon>
              Нарушения и риски
            </v-card-title>
            <v-card-text>
              <v-list>
                <v-list-item @click="showViolationDetails('speed')" style="cursor: pointer;" hover>
                  <v-list-item-avatar>
                    <v-icon :color="analytics.safety.speedViolations > 5 ? 'error' : 'success'">
                      {{ analytics.safety.speedViolations > 5 ? 'mdi-alert' : 'mdi-check-circle' }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>Превышения скорости</v-list-item-title>
                    <v-list-item-subtitle>{{ analytics.safety.speedViolations }} случаев за
                      период</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip small :color="analytics.safety.speedViolations > 5 ? 'error' : 'success'">
                      {{ analytics.safety.speedViolations > 5 ? 'Критично' : 'Норма' }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item @click="showViolationDetails('route')" style="cursor: pointer;" hover>
                  <v-list-item-avatar>
                    <v-icon :color="analytics.antifraud.routeDeviations > 3 ? 'warning' : 'success'">
                      {{ analytics.antifraud.routeDeviations > 3 ? 'mdi-alert' : 'mdi-check-circle' }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>Подозрительные маршруты</v-list-item-title>
                    <v-list-item-subtitle>{{ analytics.antifraud.routeDeviations }} отклонений от оптимального
                      пути</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip small :color="analytics.antifraud.routeDeviations > 3 ? 'warning' : 'success'">
                      {{ analytics.antifraud.routeDeviations > 3 ? 'Внимание' : 'Норма' }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item @click="showViolationDetails('stops')" style="cursor: pointer;" hover>
                  <v-list-item-avatar>
                    <v-icon :color="analytics.antifraud.suspiciousStops > 2 ? 'warning' : 'success'">
                      {{ analytics.antifraud.suspiciousStops > 2 ? 'mdi-alert' : 'mdi-check-circle' }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>Необъяснимые остановки</v-list-item-title>
                    <v-list-item-subtitle>{{ analytics.antifraud.suspiciousStops }} подозрительных
                      остановок</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip small :color="analytics.antifraud.suspiciousStops > 2 ? 'warning' : 'success'">
                      {{ analytics.antifraud.suspiciousStops > 2 ? 'Проверить' : 'Норма' }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card>
            <v-card-title>
              <v-icon left color="success">mdi-chart-line</v-icon>
              Положительные показатели
            </v-card-title>
            <v-card-text>
              <v-list>
                <v-list-item>
                  <v-list-item-avatar>
                    <v-icon color="success">mdi-check-circle</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>Завершенные поездки</v-list-item-title>
                    <v-list-item-subtitle>{{ analytics.completionRate }}% успешного завершения</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <div class="text-h6 success--text">{{ analytics.completedOrders }}</div>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item>
                  <v-list-item-avatar>
                    <v-icon color="info">mdi-map-marker-distance</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>Общий пробег</v-list-item-title>
                    <v-list-item-subtitle>За выбранный период</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <div class="text-h6 info--text">{{ formatDistance(analytics.safety.totalDistance) }}</div>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item>
                  <v-list-item-avatar>
                    <v-icon color="primary">mdi-map-marker-path</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>Оптимальность маршрутов</v-list-item-title>
                    <v-list-item-subtitle>Процент использования оптимальных путей</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <div class="text-h6 primary--text">{{ analytics.antifraud.avgRouteEfficiency }}%</div>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Рекомендации -->
      <v-card class="mt-4" v-if="getSafetyRecommendations().length > 0">
        <v-card-title>
          <v-icon left color="warning">mdi-lightbulb-on</v-icon>
          Рекомендации по улучшению
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item v-for="(recommendation, index) in getSafetyRecommendations()" :key="index">
              <v-list-item-avatar>
                <v-icon :color="recommendation.priority === 'high' ? 'error' : 'warning'">
                  {{ recommendation.icon }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ recommendation.title }}</v-list-item-title>
                <v-list-item-subtitle>{{ recommendation.description }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </div>

    <!-- Вкладка управления водителем -->
    <div v-if="client?.yandex_driver_id && activeTab === 4">
      <!-- Статус водителя -->
      <v-card class="mb-4">
        <v-card-title>
          <v-icon left color="primary">mdi-account-cog</v-icon>
          Статус водителя
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-select v-model="driverStatus" :items="statusOptions" label="Статус работы" item-text="text"
                item-value="value" :disabled="updatingStatus" @change="updateDriverStatus"></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-btn color="primary" :loading="syncing" @click="syncDriverData" class="mt-2">
                <v-icon left>mdi-sync</v-icon>
                Синхронизировать данные
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Настройки баланса -->
      <v-card class="mb-4">
        <v-card-title>
          <v-icon left color="primary">mdi-currency-usd</v-icon>
          Настройки баланса
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="balanceLimit" label="Лимит баланса" type="number" suffix="₽"
                :disabled="updatingBalance" @blur="updateBalanceLimit"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-switch v-model="blockOrdersOnLowBalance" label="Блокировать заказы при низком балансе"
                :disabled="updatingBalance" @change="updateBlockOrdersSetting"></v-switch>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="paymentServiceId" label="ID платежного сервиса" :disabled="updatingBalance"
                @blur="updatePaymentServiceId"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Настройки заказов -->
      <v-card class="mb-4">
        <v-card-title>
          <v-icon left color="primary">mdi-car-multiple</v-icon>
          Настройки заказов
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-select v-model="workRuleId" :items="workRules" label="Условия работы" item-text="name" item-value="id"
                :disabled="updatingWorkRule" @click:prepend-inner="loadWorkRules" @change="updateWorkRule"></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <div class="d-flex flex-column">
                <v-switch v-model="platformOrders" label="Заказы от платформы" :disabled="updatingOrderProviders"
                  @change="updateOrderProviders"></v-switch>
                <v-switch v-model="partnerOrders" label="Заказы от партнера" :disabled="updatingOrderProviders"
                  @change="updateOrderProviders"></v-switch>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Информация о водителе -->
      <v-card class="mb-4">
        <v-card-title>
          <v-icon left color="primary">mdi-information</v-icon>
          Информация о водителе
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="driverInfo.fullName" label="Полное имя" readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="driverInfo.phone" label="Телефон" readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="driverInfo.licenseNumber" label="Номер водительского удостоверения"
                readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="driverInfo.hireDate" label="Дата найма" readonly></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Действия -->
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-tools</v-icon>
          Действия
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-btn color="success" :loading="refreshingBalance" @click="refreshDriverBalance" block>
                <v-icon left>mdi-cash-refresh</v-icon>
                Обновить баланс
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn color="info" :loading="loadingDriverDetails" @click="loadDriverDetails" block>
                <v-icon left>mdi-account-details</v-icon>
                Загрузить детали
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn color="warning" :loading="resettingDriver" @click="resetDriverSettings" block>
                <v-icon left>mdi-restore</v-icon>
                Сбросить настройки
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- Модальное окно с деталями заказа -->
    <v-dialog v-model="orderDetailsDialog" max-width="800px">
      <v-card v-if="selectedOrder">
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-receipt</v-icon>
          Детали заказа #{{ selectedOrder.short_id }}
          <v-spacer></v-spacer>
          <v-btn icon @click="orderDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <!-- Основная информация -->
            <v-col cols="12" md="6">
              <h3 class="text-h6 mb-3">Основная информация</h3>
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Статус</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip small :color="getStatusColor(selectedOrder.status)">
                        {{ getStatusText(selectedOrder.status) }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Создан</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDateTime(selectedOrder.created_at) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedOrder.ended_at">
                  <v-list-item-content>
                    <v-list-item-title>Завершен</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDateTime(selectedOrder.ended_at) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Стоимость</v-list-item-title>
                    <v-list-item-subtitle class="text-h6 font-weight-bold success--text">
                      {{ formatPrice(selectedOrder.cost) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <!-- Маршрут -->
            <v-col cols="12" md="6">
              <h3 class="text-h6 mb-3">Маршрут</h3>
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Откуда</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedOrder.route_from }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Куда</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedOrder.route_to }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedOrder.distance">
                  <v-list-item-content>
                    <v-list-item-title>Пробег</v-list-item-title>
                    <v-list-item-subtitle>{{ toKilometers(selectedOrder.distance) }} км</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedOrder.car_info">
                  <v-list-item-content>
                    <v-list-item-title>Автомобиль</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedOrder.car_info }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedOrder.payment_method">
                  <v-list-item-content>
                    <v-list-item-title>Способ оплаты</v-list-item-title>
                    <v-list-item-subtitle>{{ getPaymentMethodText(selectedOrder.payment_method)
                      }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedOrder.cancellation_reason">
                  <v-list-item-content>
                    <v-list-item-title>Причина отмены</v-list-item-title>
                    <v-list-item-subtitle>{{ getCancellationReasonText(selectedOrder.cancellation_reason)
                      }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <!-- Трек заказа -->
          <v-row v-if="selectedOrderTrack.length > 0" class="mt-4">
            <v-col cols="12">
              <h3 class="text-h6 mb-3 d-flex align-center">
                <v-icon left color="primary">mdi-map-marker-path</v-icon>
                Маршрут поездки
                <v-spacer></v-spacer>
                <v-chip small color="primary">
                  {{ selectedOrderTrack.length }} точек
                </v-chip>
              </h3>

              <!-- Статистика по треку -->
              <v-row class="mb-3">
                <v-col cols="6" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-2">
                      <div class="text-caption">Общее расстояние</div>
                      <div class="text-h6 font-weight-bold">{{ formatDistance(trackStats.totalDistance) }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-2">
                      <div class="text-caption">Макс. скорость</div>
                      <div class="text-h6 font-weight-bold">{{ trackStats.maxSpeed }} км/ч</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-2">
                      <div class="text-caption">Средняя скорость</div>
                      <div class="text-h6 font-weight-bold">{{ trackStats.avgSpeed }} км/ч</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-2">
                      <div class="text-caption">Время ожидания</div>
                      <div class="text-h6 font-weight-bold">{{ trackStats.waitingTime }} мин</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Детальная информация по фазам поездки -->
              <v-expansion-panels class="mb-3">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div class="d-flex align-center">
                      <v-icon left color="info">mdi-information-outline</v-icon>
                      <span class="font-weight-bold">Детальная информация по этапам</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <tbody>
                          <tr>
                            <td><v-icon left small color="primary">mdi-car-arrow-right</v-icon>В пути к клиенту</td>
                            <td class="text-right font-weight-bold">{{ trackStats.drivingTime }} мин</td>
                          </tr>
                          <tr>
                            <td><v-icon left small color="warning">mdi-clock-outline</v-icon>Ожидание клиента</td>
                            <td class="text-right font-weight-bold">{{ trackStats.waitingTime }} мин</td>
                          </tr>
                          <tr>
                            <td><v-icon left small color="success">mdi-car</v-icon>Перевозка</td>
                            <td class="text-right font-weight-bold">{{ trackStats.transportingTime }} мин</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <!-- Примечание о карте -->
              <v-alert type="info" text dense class="mb-0">
                <div class="text-caption">
                  <v-icon small left>mdi-map</v-icon>
                  Визуализация маршрута на карте будет доступна в следующем обновлении
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Загрузка трека -->
          <v-row v-else-if="isLoadingTrack">
            <v-col cols="12">
              <v-alert type="info" text>
                <div class="d-flex align-center">
                  <v-progress-circular indeterminate size="20" width="2" class="mr-2"></v-progress-circular>
                  Загрузка маршрута поездки...
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Кнопка загрузки трека если его еще нет -->
          <v-row v-else-if="selectedOrder && selectedOrder.status === 'complete'" class="mt-4">
            <v-col cols="12">
              <v-btn color="primary" outlined block @click="loadOrderTrack(selectedOrder.id)">
                <v-icon left>mdi-map-marker-path</v-icon>
                Показать маршрут поездки
              </v-btn>
            </v-col>
          </v-row>

          <!-- Транзакции заказа -->
          <v-row v-if="orderTransactions.length > 0" class="mt-4">
            <v-col cols="12">
              <h3 class="text-h6 mb-3 d-flex align-center">
                <v-icon left color="primary">mdi-credit-card</v-icon>
                Транзакции по заказу
                <v-spacer></v-spacer>
                <v-chip small color="primary">
                  {{ orderTransactions.length }}
                </v-chip>
              </h3>

              <v-data-table :headers="transactionHeaders" :items="orderTransactions" :loading="isLoadingTransactions"
                :items-per-page="10" class="elevation-1" dense>

                <!-- ID транзакции -->
                <template v-slot:item.id="{ item }">
                  <v-chip small color="info" text-color="white">
                    {{ item.id }}
                  </v-chip>
                </template>

                <!-- Дата -->
                <template v-slot:item.event_at="{ item }">
                  {{ formatDateTime(item.event_at) }}
                </template>

                <!-- Сумма -->
                <template v-slot:item.amount="{ item }">
                  <span class="font-weight-bold" :class="item.amount > 0 ? 'success--text' : 'error--text'">
                    {{ formatPrice(item.amount) }}
                  </span>
                </template>

                <!-- Категория -->
                <template v-slot:item.category_name="{ item }">
                  <v-chip small color="grey" text-color="white">
                    {{ item.category_name }}
                  </v-chip>
                </template>

                <!-- Описание -->
                <template v-slot:item.description="{ item }">
                  <div class="text-truncate" style="max-width: 200px;" :title="item.description">
                    {{ item.description }}
                  </div>
                </template>

                <!-- Создатель -->
                <template v-slot:item.created_by="{ item }">
                  <div class="text-truncate" style="max-width: 150px;" :title="item.created_by">
                    {{ item.created_by }}
                  </div>
                </template>
              </v-data-table>
            </v-col>
          </v-row>

          <!-- Сообщение если транзакций нет -->
          <v-row v-else-if="!isLoadingTransactions">
            <v-col cols="12">
              <v-alert type="info" outlined>
                <v-icon left>mdi-information</v-icon>
                По данному заказу транзакций не найдено
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Диалог деталей нарушений -->
    <v-dialog v-model="violationDetailsDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left :color="getViolationIconColor(selectedViolationType)">
            {{ getViolationIcon(selectedViolationType) }}
          </v-icon>
          {{ getViolationTitle(selectedViolationType) }}
          <v-spacer></v-spacer>
          <v-btn icon @click="violationDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="violationDetails.length === 0" class="text-center py-8">
            <v-icon size="64" color="grey">mdi-check-circle</v-icon>
            <div class="text-h6 mt-4">Нарушений не найдено</div>
            <div class="text-caption grey--text">За выбранный период нарушений данного типа не обнаружено</div>
          </div>

          <div v-else>
            <div class="text-caption mb-4">
              Найдено {{ violationDetails.length }} {{ getViolationCountText(selectedViolationType) }}
            </div>

            <!-- Детали превышений скорости -->
            <div v-if="selectedViolationType === 'speed'">
              <v-list>
                <v-list-item v-for="(violation, index) in violationDetails" :key="index" class="mb-2"
                  style="border: 1px solid #e0e0e0; border-radius: 8px;">
                  <v-list-item-content>
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="text-h6">{{ violation.orderNumber }}</div>
                        <div class="text-caption">{{ violation.date }} в {{ violation.time }}</div>
                      </div>
                      <div class="text-right">
                        <div class="text-h5 font-weight-bold" :class="getSpeedColor(violation.speed, violation.limit)">
                          {{ violation.speed }} км/ч
                        </div>
                        <div class="text-caption">лимит: {{ violation.limit }} км/ч</div>
                      </div>
                    </div>

                    <v-divider class="my-2"></v-divider>

                    <div class="d-flex justify-space-between align-center">
                      <div>
                        <div class="text-caption">Превышение: +{{ violation.speed - violation.limit }} км/ч</div>
                        <div class="text-caption">Продолжительность: {{ violation.duration }}</div>
                      </div>
                      <v-chip small :color="getSeverityColor(violation.severity)"
                        :text-color="getSeverityTextColor(violation.severity)">
                        {{ violation.severity }}
                      </v-chip>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <!-- Детали отклонений маршрутов -->
            <div v-if="selectedViolationType === 'route'">
              <v-list>
                <v-list-item v-for="(deviation, index) in violationDetails" :key="index" class="mb-2"
                  style="border: 1px solid #e0e0e0; border-radius: 8px;">
                  <v-list-item-content>
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="text-h6">{{ deviation.orderNumber }}</div>
                        <div class="text-caption">{{ deviation.date }}</div>
                      </div>
                      <div class="text-right">
                        <div class="text-h5 font-weight-bold" :class="getEfficiencyColor(deviation.efficiency)">
                          {{ deviation.efficiency }}%
                        </div>
                        <div class="text-caption">эффективность</div>
                      </div>
                    </div>

                    <v-divider class="my-2"></v-divider>

                    <div class="d-flex justify-space-between align-center">
                      <div>
                        <div class="text-caption">Отклонение: {{ deviation.deviation }} м</div>
                        <div class="text-caption">
                          Ожидаемо: {{ deviation.expectedDistance }} м →
                          Фактически: {{ deviation.actualDistance }} м
                        </div>
                      </div>
                      <v-chip small :color="getSeverityColor(deviation.severity)"
                        :text-color="getSeverityTextColor(deviation.severity)">
                        {{ deviation.severity }}
                      </v-chip>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <!-- Детали подозрительных остановок -->
            <div v-if="selectedViolationType === 'stops'">
              <v-list>
                <v-list-item v-for="(stop, index) in violationDetails" :key="index" class="mb-2"
                  style="border: 1px solid #e0e0e0; border-radius: 8px;">
                  <v-list-item-content>
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="text-h6">{{ stop.orderNumber }}</div>
                        <div class="text-caption">{{ stop.date }}</div>
                      </div>
                      <div class="text-right">
                        <div class="text-h5 font-weight-bold" :class="getDurationColor(stop.duration)">
                          {{ stop.duration }}
                        </div>
                        <div class="text-caption">продолжительность</div>
                      </div>
                    </div>

                    <v-divider class="my-2"></v-divider>

                    <div class="d-flex justify-space-between align-center">
                      <div>
                        <div class="text-caption">Время: {{ stop.startTime }} - {{ stop.endTime }}</div>
                        <div class="text-caption">Причина: {{ stop.reason }}</div>
                      </div>
                      <v-chip small :color="getSeverityColor(stop.severity)"
                        :text-color="getSeverityTextColor(stop.severity)">
                        {{ stop.severity }}
                      </v-chip>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="violationDetailsDialog = false" color="primary">
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { setMomentLocale } from '@/utils/localeInfra'
import { isRateLimitedError, rateLimitRetrySeconds, rateLimitedMessage } from '@/utils/networkErrors'
import 'moment/locale/ru'

export default {
  name: 'TaxiDriverInfo',
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Вкладки
      activeTab: 0,

      // Данные
      orders: [],
      selectedOrder: null,
      orderTransactions: [],
      allTransactions: [],

      // Треки заказов (кэш)
      orderTracksCache: {}, // { orderId: { track: [], stats: {}, loadedAt: timestamp } }
      selectedOrderTrack: [],
      trackStats: {
        totalDistance: 0,
        maxSpeed: 0,
        avgSpeed: 0,
        waitingTime: 0,
        drivingTime: 0,
        transportingTime: 0
      },

      // Пагинация
      ordersCursor: null,
      transactionsCursor: null,
      hasMoreOrders: false,
      hasMoreTransactions: false,

      // Состояния загрузки
      isLoading: false,
      isLoadingTransactions: false,
      isLoadingMoreOrders: false,
      isLoadingMoreTransactions: false,
      isLoadingAnalytics: false,
      isLoadingTrack: false,

      // Аналитика
      analytics: {
        totalOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        totalRevenue: 0,
        avgOrderCost: 0,
        maxOrderCost: 0,
        totalTransactions: 0,
        totalDeposits: 0,
        totalWithdrawals: 0,
        balanceChange: 0,
        completionRate: 0,
        avgOrdersPerDay: 0,
        avgRevenuePerDay: 0,
        bestDay: null,
        antifraud: {
          maxCost: 0,
          maxDuration: 0,
          minDuration: 0,
          maxOrdersPerHour: 0,
          routeDeviations: 0, // Количество маршрутов с отклонениями
          suspiciousStops: 0, // Подозрительные остановки
          avgRouteEfficiency: 0 // Средняя эффективность маршрутов
        },
        safety: {
          speedViolations: 0, // Превышения скорости
          avgSpeed: 0, // Средняя скорость
          drivingScore: 0, // Оценка вождения (0-100)
          totalDistance: 0 // Общее расстояние
        }
      },
      analyticsLoadingProgress: {
        ordersLoaded: 0,
        transactionsLoaded: 0,
        tracksLoaded: 0,
        tracksTotal: 0,
        isLoading: false
      },

      // Диалоги
      orderDetailsDialog: false,
      violationDetailsDialog: false,
      selectedViolationType: null,
      violationDetails: [],

      // Периоды
      selectedPeriod: 'week',
      customStartDate: '',
      customEndDate: '',
      today: moment().format('YYYY-MM-DD'),

      // Опции периодов
      periodOptions: [
        { text: 'За последние сутки', value: 'day' },
        { text: 'За неделю', value: 'week' },
        { text: 'За месяц', value: 'month' },
        { text: 'Произвольный период', value: 'custom' }
      ],

      // Заголовки таблицы
      headers: [
        { text: 'ID', value: 'id', sortable: false, width: '80px' },
        { text: 'Статус', value: 'status', sortable: false, width: '120px' },
        { text: 'Создан', value: 'created_at', sortable: false, width: '150px' },
        { text: 'Завершен', value: 'ended_at', sortable: false, width: '150px' },
        { text: 'Стоимость', value: 'cost', sortable: false, width: '100px' },
        { text: 'Категория', value: 'category', sortable: false, width: '120px' },
        { text: 'Откуда', value: 'route_from', sortable: false, width: '200px' },
        { text: 'Куда', value: 'route_to', sortable: false, width: '200px' },
        { text: 'Авто', value: 'car_info', sortable: false, width: '150px' },
        { text: 'Действия', value: 'actions', sortable: false, width: '80px' }
      ],

      // Заголовки таблицы транзакций заказа
      transactionHeaders: [
        { text: 'ID', value: 'id', sortable: false, width: '100px' },
        { text: 'Дата', value: 'event_at', sortable: false, width: '150px' },
        { text: 'Сумма', value: 'amount', sortable: false, width: '100px' },
        { text: 'Категория', value: 'category_name', sortable: false, width: '150px' },
        { text: 'Описание', value: 'description', sortable: false },
        { text: 'Создатель', value: 'created_by', sortable: false, width: '150px' }
      ],

      // Заголовки таблицы всех транзакций
      allTransactionsHeaders: [
        { text: 'ID', value: 'id', sortable: false, width: '120px' },
        { text: 'Дата', value: 'event_at', sortable: false, width: '160px' },
        { text: 'Сумма', value: 'amount', sortable: false, width: '120px' },
        { text: 'Валюта', value: 'currency_code', sortable: false, width: '80px' },
        { text: 'Категория', value: 'category_name', sortable: false, width: '180px' },
        { text: 'Описание', value: 'description', sortable: false },
        { text: 'Создатель', value: 'created_by', sortable: false, width: '150px' }
      ],

      // Управление водителем
      driverStatus: 'working',
      statusOptions: [
        { text: 'Работает', value: 'working' },
        { text: 'Не работает', value: 'not_working' },
        { text: 'Уволен', value: 'fired' }
      ],
      balanceLimit: 0,
      blockOrdersOnLowBalance: false,
      paymentServiceId: '',
      workRuleId: '',
      workRules: [],
      platformOrders: true,
      partnerOrders: false,

      // Информация о водителе
      driverInfo: {
        fullName: '',
        phone: '',
        licenseNumber: '',
        hireDate: ''
      },

      // Состояния загрузки для управления
      updatingStatus: false,
      syncing: false,
      updatingBalance: false,
      updatingWorkRule: false,
      updatingOrderProviders: false,
      refreshingBalance: false,
      loadingDriverDetails: false,
      resettingDriver: false
    }
  },

  mounted() {
    this.setupPeriods()
  },

  watch: {
    client: {
      handler(newClient) {
        if (newClient?.yandex_driver_id) {
          // Загружаем данные только если еще не загружали
          if (this.activeTab === 0 && this.orders.length === 0 && !this.ordersCursor) {
            this.loadOrders()
          } else if (this.activeTab === 1 && this.allTransactions.length === 0 && !this.transactionsCursor) {
            this.loadTransactions()
          } else if (this.activeTab === 2) {
            // Для аналитики всегда загружаем при появлении водителя
            this.loadAnalytics()
          } else if (this.activeTab === 3) {
            // Для безопасности загружаем аналитику безопасности
            this.loadSafetyAnalytics()
          } else if (this.activeTab === 4) {
            // Для управления загружаем детали водителя
            this.loadDriverDetails()
          }
        }
      },
      immediate: true
    },
    activeTab(newTab) {
      if (this.client?.yandex_driver_id) {
        // Загружаем данные только если это первая загрузка для вкладки
        if (newTab === 0 && this.orders.length === 0 && !this.ordersCursor) {
          this.loadOrders()
        } else if (newTab === 1 && this.allTransactions.length === 0 && !this.transactionsCursor) {
          this.loadTransactions()
        } else if (newTab === 2) {
          // Для аналитики всегда загружаем свежие данные
          this.loadAnalytics()
        } else if (newTab === 3) {
          // Для безопасности загружаем аналитику безопасности
          this.loadSafetyAnalytics()
        } else if (newTab === 4) {
          // Для управления загружаем детали водителя
          this.loadDriverDetails()
        }
      }
    }
  },

  methods: {
    // Загрузка первой страницы заказов водителя
    async loadOrders(resetData = true) {
      if (!this.client?.yandex_driver_id) {
        this.$notify('Предупреждение', 'У водителя нет Yandex.Taxi ID', 'warning', 3000)
        return
      }

      this.isLoading = true

      // Сбрасываем данные при новом поиске
      if (resetData) {
        this.orders = []
        this.ordersCursor = null
        this.hasMoreOrders = false
      }

      try {
        const dateRange = this.getDateRange()

        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.client.id}/orders`,
          {
            yandex_integration: {
              date_from: dateRange.from,
              date_to: dateRange.to,
              limit: 100,
              cursor: null // Всегда начинаем с первой страницы
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.orders = response.data.data.orders || []
          this.ordersCursor = response.data.data.cursor
          this.hasMoreOrders = this.ordersCursor && this.ordersCursor.trim() !== ''
        } else {
          this.orders = []
          this.$notify('Ошибка', response.data.error || 'Не удалось загрузить поездки', 'error', 5000)
        }
      } catch (error) {
        console.error('Error loading orders:', error)
        this.$notify('Ошибка', 'Ошибка загрузки поездок', 'error', 5000)
        this.orders = []
      } finally {
        this.isLoading = false
      }
    },

    // Загрузка следующей страницы заказов
    async loadMoreOrders() {
      if (!this.ordersCursor || this.isLoadingMoreOrders) return

      this.isLoadingMoreOrders = true
      try {
        const dateRange = this.getDateRange()

        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.client.id}/orders`,
          {
            yandex_integration: {
              date_from: dateRange.from,
              date_to: dateRange.to,
              limit: 100,
              cursor: this.ordersCursor
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          const newOrders = response.data.data.orders || []
          this.orders = this.orders.concat(newOrders)
          this.ordersCursor = response.data.data.cursor
          this.hasMoreOrders = this.ordersCursor && this.ordersCursor.trim() !== ''
        } else {
          this.$notify('Ошибка', response.data.error || 'Не удалось загрузить поездки', 'error', 5000)
        }
      } catch (error) {
        console.error('Error loading more orders:', error)
        this.$notify('Ошибка', 'Ошибка загрузки поездок', 'error', 5000)
      } finally {
        this.isLoadingMoreOrders = false
      }
    },

    // Настройка периодов
    setupPeriods() {
      setMomentLocale(this.$i18n.locale)
    },

    // Получение диапазона дат
    getDateRange() {
      const now = moment()

      switch (this.selectedPeriod) {
        case 'day':
          return {
            from: now.clone().subtract(1, 'day').toISOString(),
            to: now.toISOString()
          }
        case 'week':
          return {
            from: now.clone().subtract(1, 'week').toISOString(),
            to: now.toISOString()
          }
        case 'month':
          return {
            from: now.clone().subtract(1, 'month').toISOString(),
            to: now.toISOString()
          }
        case 'custom':
          return {
            from: moment(this.customStartDate).startOf('day').toISOString(),
            to: moment(this.customEndDate).endOf('day').toISOString()
          }
        default:
          return {
            from: now.clone().subtract(1, 'week').toISOString(),
            to: now.toISOString()
          }
      }
    },

    // Загрузка первой страницы транзакций водителя
    async loadTransactions(resetData = true) {
      if (!this.client?.yandex_driver_id) {
        this.$notify('Предупреждение', 'У водителя нет Yandex.Taxi ID', 'warning', 3000)
        return
      }

      this.isLoadingTransactions = true

      // Сбрасываем данные при новом поиске
      if (resetData) {
        this.allTransactions = []
        this.transactionsCursor = null
        this.hasMoreTransactions = false
      }

      try {
        const dateRange = this.getDateRange()

        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.client.id}/transactions`,
          {
            yandex_integration: {
              date_from: dateRange.from,
              date_to: dateRange.to,
              limit: 100,
              cursor: null // Всегда начинаем с первой страницы
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.allTransactions = response.data.data.transactions || []
          this.transactionsCursor = response.data.data.cursor
          this.hasMoreTransactions = this.transactionsCursor && this.transactionsCursor.trim() !== ''
        } else {
          this.allTransactions = []
          this.$notify('Ошибка', response.data.error || 'Не удалось загрузить транзакции', 'error', 5000)
        }
      } catch (error) {
        console.error('Error loading transactions:', error)
        this.$notify('Ошибка', 'Ошибка загрузки транзакций', 'error', 5000)
        this.allTransactions = []
      } finally {
        this.isLoadingTransactions = false
      }
    },

    // Загрузка следующей страницы транзакций
    async loadMoreTransactions() {
      if (!this.transactionsCursor || this.isLoadingMoreTransactions) return

      this.isLoadingMoreTransactions = true
      try {
        const dateRange = this.getDateRange()

        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.client.id}/transactions`,
          {
            yandex_integration: {
              date_from: dateRange.from,
              date_to: dateRange.to,
              limit: 100,
              cursor: this.transactionsCursor
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          const newTransactions = response.data.data.transactions || []
          this.allTransactions = this.allTransactions.concat(newTransactions)
          this.transactionsCursor = response.data.data.cursor
          this.hasMoreTransactions = this.transactionsCursor && this.transactionsCursor.trim() !== ''
        } else {
          this.$notify('Ошибка', response.data.error || 'Не удалось загрузить транзакции', 'error', 5000)
        }
      } catch (error) {
        console.error('Error loading more transactions:', error)
        this.$notify('Ошибка', 'Ошибка загрузки транзакций', 'error', 5000)
      } finally {
        this.isLoadingMoreTransactions = false
      }
    },

    // Загрузка аналитики
    async loadAnalytics() {
      this.isLoadingAnalytics = true
      this.analyticsLoadingProgress.isLoading = true
      this.analyticsLoadingProgress.ordersLoaded = 0
      this.analyticsLoadingProgress.transactionsLoaded = 0
      this.analyticsLoadingProgress.tracksLoaded = 0
      this.analyticsLoadingProgress.tracksTotal = 0

      try {
        // Небольшая задержка перед началом загрузки для избежания rate limit
        await this.sleep(500)

        // Для аналитики загружаем ВСЕ данные за период
        // Загружаем поездки и транзакции последовательно для избежания rate limit
        const ordersData = await this.loadAllOrdersForAnalytics()
        await this.sleep(1000) // Задержка между загрузкой поездок и транзакций
        const transactionsData = await this.loadAllTransactionsForAnalytics()

        // Загружаем выборку треков для расчёта метрик безопасности
        await this.sleep(1000) // Задержка перед загрузкой треков
        await this.loadTracksForAnalytics(ordersData)

        // Вычисляем аналитику на основе ВСЕХ загруженных данных
        this.calculateAnalytics(ordersData, transactionsData)

        const tracksCount = Object.keys(this.orderTracksCache).length
        this.$notify('Успешно', `Загружено ${ordersData.length} поездок, ${transactionsData.length} транзакций и ${tracksCount} треков`, 'success', 3000)
      } catch (error) {
        console.error('Error loading analytics:', error)
        this.$notify('Ошибка', 'Ошибка загрузки аналитики', 'error', 5000)
      } finally {
        this.isLoadingAnalytics = false
        this.analyticsLoadingProgress.isLoading = false
      }
    },

    // Задержка между запросами для избежания rate limiting
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    // === МЕТОДЫ УПРАВЛЕНИЯ ВОДИТЕЛЕМ ===

    // Обновление статуса водителя
    async updateDriverStatus() {
      if (!this.client?.yandex_driver_id) return

      this.updatingStatus = true
      try {
        const response = await this.axios.put(
          `/api/v1/taxi/drivers/${this.client.id}/status`,
          {
            driver_management: {
              work_status: this.driverStatus
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.$notify('Успех', 'Статус водителя обновлен', 'success')
          // Даем время API на обновление данных - увеличиваем задержку
          await this.sleep(3000)
          await this.loadDriverDetails() // Перезагружаем данные
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось обновить статус', 'error')
        }
      } catch (error) {
        console.error('Error updating driver status:', error)
        this.$notify('Ошибка', 'Не удалось обновить статус водителя', 'error')
      } finally {
        this.updatingStatus = false
      }
    },

    // Синхронизация данных водителя с Yandex
    async syncDriverData() {
      if (!this.client?.yandex_driver_id) return

      this.syncing = true
      try {
        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.client.id}/sync`,
          {},
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.$notify('Успех', 'Данные водителя синхронизированы', 'success')
          await this.loadDriverDetails()
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось синхронизировать данные', 'error')
        }
      } catch (error) {
        console.error('Error syncing driver data:', error)
        this.$notify('Ошибка', 'Не удалось синхронизировать данные водителя', 'error')
      } finally {
        this.syncing = false
      }
    },

    // Обновление лимита баланса
    async updateBalanceLimit() {
      if (!this.client?.yandex_driver_id) return

      this.updatingBalance = true
      try {
        const response = await this.axios.put(
          `/api/v1/taxi/drivers/${this.client.id}/balance-settings`,
          {
            driver_management: {
              balance_limit: this.balanceLimit.toString(),
              block_orders_on_balance_below_limit: this.blockOrdersOnLowBalance,
              payment_service_id: this.paymentServiceId
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.$notify('Успех', 'Настройки баланса обновлены', 'success')
          // Даем время API на обновление данных - увеличиваем задержку
          await this.sleep(3000)
          await this.loadDriverDetails() // Перезагружаем данные
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось обновить настройки баланса', 'error')
        }
      } catch (error) {
        console.error('Error updating balance settings:', error)
        this.$notify('Ошибка', 'Не удалось обновить настройки баланса', 'error')
      } finally {
        this.updatingBalance = false
      }
    },

    // Обновление настройки блокировки заказов
    async updateBlockOrdersSetting() {
      await this.updateBalanceLimit()
    },

    // Обновление ID платежного сервиса
    async updatePaymentServiceId() {
      await this.updateBalanceLimit()
    },

    // Загрузка условий работы
    async loadWorkRules() {
      try {
        const response = await this.axios.get(
          '/api/v1/taxi/work-rules',
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          console.log('Work rules response:', response.data)
          console.log('Work rules data:', response.data.data)

          const workRulesData = response.data.data.data
          console.log('Actual work rules array:', workRulesData)
          console.log('Is array?', Array.isArray(workRulesData))

          if (Array.isArray(workRulesData)) {
            this.workRules = workRulesData.map(rule => ({
              id: rule.id,
              name: rule.name,
              is_enabled: rule.is_enabled
            }))
          } else {
            console.error('Work rules data is not an array:', workRulesData)
            this.$notify('Ошибка', 'Неверный формат данных условий работы', 'error')
          }
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось загрузить условия работы', 'error')
        }
      } catch (error) {
        console.error('Error loading work rules:', error)
        this.$notify('Ошибка', 'Не удалось загрузить условия работы', 'error')
      }
    },

    // Обновление условий работы
    async updateWorkRule() {
      if (!this.client?.yandex_driver_id || !this.workRuleId) return

      this.updatingWorkRule = true
      try {
        const response = await this.axios.put(
          `/api/v1/taxi/drivers/${this.client.id}/work-rule`,
          {
            driver_management: {
              work_rule_id: this.workRuleId
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.$notify('Успех', 'Условия работы обновлены', 'success')
          // Даем время API на обновление данных - увеличиваем задержку
          await this.sleep(3000)
          await this.loadDriverDetails() // Перезагружаем данные
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось обновить условия работы', 'error')
        }
      } catch (error) {
        console.error('Error updating work rule:', error)
        this.$notify('Ошибка', 'Не удалось обновить условия работы', 'error')
      } finally {
        this.updatingWorkRule = false
      }
    },

    // Обновление провайдеров заказов
    async updateOrderProviders() {
      if (!this.client?.yandex_driver_id) return

      this.updatingOrderProviders = true
      try {
        const response = await this.axios.put(
          `/api/v1/taxi/drivers/${this.client.id}/order-providers`,
          {
            driver_management: {
              order_provider: {
                platform: this.platformOrders,
                partner: this.partnerOrders
              }
            }
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.$notify('Успех', 'Настройки заказов обновлены', 'success')
          // Даем время API на обновление данных - увеличиваем задержку
          await this.sleep(3000)
          await this.loadDriverDetails() // Перезагружаем данные
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось обновить настройки заказов', 'error')
        }
      } catch (error) {
        console.error('Error updating order providers:', error)
        this.$notify('Ошибка', 'Не удалось обновить настройки заказов', 'error')
      } finally {
        this.updatingOrderProviders = false
      }
    },

    // Обновление баланса водителя
    async refreshDriverBalance() {
      if (!this.client?.yandex_driver_id) return

      this.refreshingBalance = true
      try {
        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.client.id}/refresh-balance`,
          {},
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.$notify('Успех', 'Баланс водителя обновлен', 'success')
          await this.loadDriverDetails()
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось обновить баланс', 'error')
        }
      } catch (error) {
        console.error('Error refreshing driver balance:', error)
        this.$notify('Ошибка', 'Не удалось обновить баланс водителя', 'error')
      } finally {
        this.refreshingBalance = false
      }
    },

    // Загрузка детальной информации о водителе
    async loadDriverDetails() {
      if (!this.client?.yandex_driver_id) return

      this.loadingDriverDetails = true
      try {
        const response = await this.axios.get(
          `/api/v1/taxi/drivers/${this.client.id}/details`,
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          const responseData = response.data.data
          const driverData = responseData.data

          // Отладочная информация
          console.log('Response data received:', responseData)
          console.log('Driver data:', driverData)
          console.log('Work status value:', driverData.work_status)

          // Обновляем информацию о водителе
          this.driverStatus = driverData.work_status || 'working'
          this.balanceLimit = parseFloat(driverData.balance_limit) || 0
          this.blockOrdersOnLowBalance = driverData.block_orders_on_balance_below_limit || false
          this.paymentServiceId = driverData.payment_service_id || ''
          this.workRuleId = driverData.work_rule_id || ''
          this.platformOrders = driverData.platform_orders || true
          this.partnerOrders = driverData.partner_orders || false

          // Обновляем информацию о водителе
          this.driverInfo = {
            fullName: driverData.full_name || '',
            phone: driverData.phone || '',
            licenseNumber: driverData.license_number || '',
            hireDate: driverData.hire_date || ''
          }

          this.$notify('Успех', 'Данные водителя загружены', 'success')

          // Загружаем условия работы если список пустой
          if (this.workRules.length === 0) {
            await this.loadWorkRules()
          }
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось загрузить данные водителя', 'error')
        }
      } catch (error) {
        console.error('Error loading driver details:', error)
        this.$notify('Ошибка', 'Не удалось загрузить данные водителя', 'error')
      } finally {
        this.loadingDriverDetails = false
      }
    },

    // Сброс настроек водителя
    async resetDriverSettings() {
      if (!this.client?.yandex_driver_id) return

      const confirmed = await this.$confirm({
        title: 'Сброс настроек',
        text: 'Вы уверены, что хотите сбросить все настройки водителя?',
        confirmButtonText: 'Да, сбросить',
        cancelButtonText: 'Отмена'
      })

      if (!confirmed) return

      this.resettingDriver = true
      try {
        const response = await this.axios.post(
          `/api/v1/taxi/drivers/${this.client.id}/reset-settings`,
          {},
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.$notify('Успех', 'Настройки водителя сброшены', 'success')
          await this.loadDriverDetails()
        } else {
          this.$notify('Ошибка', response.data.message || 'Не удалось сбросить настройки', 'error')
        }
      } catch (error) {
        console.error('Error resetting driver settings:', error)
        this.$notify('Ошибка', 'Не удалось сбросить настройки водителя', 'error')
      } finally {
        this.resettingDriver = false
      }
    },

    // Загрузка ВСЕХ поездок для аналитики (автоматическая пагинация с задержками)
    async loadAllOrdersForAnalytics() {
      const dateRange = this.getDateRange()
      let allOrders = []
      let cursor = null
      let hasMore = true
      const limit = 500 // Максимальный лимит по API Yandex
      let retryCount = 0
      const maxRetries = 3

      while (hasMore) {
        try {
          const response = await this.axios.post(
            `/api/v1/taxi/drivers/${this.client.id}/orders`,
            {
              yandex_integration: {
                date_from: dateRange.from,
                date_to: dateRange.to,
                limit: limit,
                cursor: cursor
              }
            },
            {
              headers: {
                Authorization: this.$store.getters.getAuthToken
              }
            }
          )

          if (response.data.success) {
            const orders = response.data.data.orders || []

            // Логирование для отладки
            console.log(`Загружена страница поездок: ${orders.length} записей`)
            console.log(`Cursor: ${response.data.data.cursor}`)
            console.log(`Всего загружено: ${allOrders.length + orders.length}`)

            // Фильтруем дубликаты по ID
            const uniqueOrders = orders.filter(order => {
              return !allOrders.some(existing => existing.id === order.id)
            })

            console.log(`После фильтрации дубликатов: ${uniqueOrders.length} новых записей`)

            allOrders = allOrders.concat(uniqueOrders)
            cursor = response.data.data.cursor

            // Останавливаем загрузку если:
            // 1. Нет cursor
            // 2. Cursor пустой
            // 3. Не получили данных (orders.length === 0)
            // 4. Получили дубликаты (uniqueOrders.length === 0)
            hasMore = cursor && cursor.trim() !== '' && orders.length > 0 && uniqueOrders.length > 0

            // Обновляем прогресс загрузки
            this.analyticsLoadingProgress.ordersLoaded = allOrders.length

            // Сбрасываем счетчик retry при успешном запросе
            retryCount = 0

            // Задержка между запросами для избежания 429 ошибки
            if (hasMore) {
              await this.sleep(1000) // 1 секунда между запросами
            }
          } else {
            hasMore = false
          }
        } catch (error) {
          retryCount++
          // 429 — подождать и повторить ту же страницу (cursor не меняется)
          if (!(await this.waitForRateLimit(error, retryCount, maxRetries))) {
            console.error('Error loading orders:', error)
            hasMore = false
          }
        }
      }

      return allOrders
    },

    // 429 при постраничной загрузке аналитики — всегда Rack::Attack (ответы
    // Яндекса API отдаёт 422). Ждём Retry-After: окно лимита до 5 минут, и
    // прежние 4/8/16 с его не переживали — загрузка молча обрывалась. Без
    // заголовка — экспоненциальная пауза. Возвращает true, если стоит повторить.
    async waitForRateLimit(error, attempt, maxRetries) {
      if (!isRateLimitedError(error)) return false
      if (attempt > maxRetries) {
        const message = rateLimitedMessage(this, error)
        this.$notify(message, '', 'warning', 6000)
        return false
      }
      const retryAfter = rateLimitRetrySeconds(error)
      const seconds = retryAfter !== null ? retryAfter : Math.pow(2, attempt) * 2
      console.log(`Rate limit exceeded. Retrying in ${seconds}s... (${attempt}/${maxRetries})`)
      this.$notify(this.$t('errors.rate_limited_auto_retry', { seconds }), '', 'warning', 6000)
      await this.sleep(seconds * 1000)
      return true
    },

    // Загрузка ВСЕХ транзакций для аналитики (автоматическая пагинация с задержками)
    async loadAllTransactionsForAnalytics() {
      const dateRange = this.getDateRange()
      let allTransactions = []
      let cursor = null
      let hasMore = true
      const limit = 500 // Максимальный лимит по API Yandex
      let retryCount = 0
      const maxRetries = 3

      while (hasMore) {
        try {
          const response = await this.axios.post(
            `/api/v1/taxi/drivers/${this.client.id}/transactions`,
            {
              yandex_integration: {
                date_from: dateRange.from,
                date_to: dateRange.to,
                limit: limit,
                cursor: cursor
              }
            },
            {
              headers: {
                Authorization: this.$store.getters.getAuthToken
              }
            }
          )

          if (response.data.success) {
            const transactions = response.data.data.transactions || []

            // Логирование для отладки
            console.log(`Загружена страница транзакций: ${transactions.length} записей`)
            console.log(`Cursor: ${response.data.data.cursor}`)
            console.log(`Всего загружено: ${allTransactions.length + transactions.length}`)

            // Фильтруем дубликаты по ID
            const uniqueTransactions = transactions.filter(transaction => {
              return !allTransactions.some(existing => existing.id === transaction.id)
            })

            console.log(`После фильтрации дубликатов: ${uniqueTransactions.length} новых записей`)

            allTransactions = allTransactions.concat(uniqueTransactions)
            cursor = response.data.data.cursor

            // Останавливаем загрузку если:
            // 1. Нет cursor
            // 2. Cursor пустой
            // 3. Не получили данных (transactions.length === 0)
            // 4. Получили дубликаты (uniqueTransactions.length === 0)
            hasMore = cursor && cursor.trim() !== '' && transactions.length > 0 && uniqueTransactions.length > 0

            // Обновляем прогресс загрузки
            this.analyticsLoadingProgress.transactionsLoaded = allTransactions.length

            // Сбрасываем счетчик retry при успешном запросе
            retryCount = 0

            // Задержка между запросами для избежания 429 ошибки
            if (hasMore) {
              await this.sleep(1000) // 1 секунда между запросами
            }
          } else {
            hasMore = false
          }
        } catch (error) {
          retryCount++
          // 429 — подождать и повторить ту же страницу (cursor не меняется)
          if (!(await this.waitForRateLimit(error, retryCount, maxRetries))) {
            console.error('Error loading transactions:', error)
            hasMore = false
          }
        }
      }

      return allTransactions
    },

    // Расчет аналитики на основе имеющихся данных
    calculateAnalytics(orders = [], transactions = []) {
      const dateRange = this.getDateRange()

      // Считаем количество дней в периоде
      const daysDiff = moment(dateRange.to).diff(moment(dateRange.from), 'days') || 1

      // Статистика по поездкам
      this.analytics.totalOrders = orders.length
      this.analytics.completedOrders = orders.filter(o => o.status === 'complete').length
      this.analytics.cancelledOrders = orders.filter(o => o.status === 'cancelled').length

      // Финансовая статистика по поездкам
      this.analytics.totalRevenue = orders
        .filter(o => o.status === 'complete')
        .reduce((sum, order) => sum + (parseFloat(order.cost) || 0), 0)

      this.analytics.avgOrderCost = this.analytics.completedOrders > 0
        ? this.analytics.totalRevenue / this.analytics.completedOrders
        : 0

      this.analytics.maxOrderCost = orders.length > 0
        ? Math.max(...orders.map(o => parseFloat(o.cost) || 0))
        : 0

      // Статистика по транзакциям
      this.analytics.totalTransactions = transactions.length
      this.analytics.totalDeposits = transactions
        .filter(t => parseFloat(t.amount) > 0)
        .reduce((sum, t) => sum + parseFloat(t.amount), 0)

      this.analytics.totalWithdrawals = Math.abs(transactions
        .filter(t => parseFloat(t.amount) < 0)
        .reduce((sum, t) => sum + parseFloat(t.amount), 0))

      this.analytics.balanceChange = this.analytics.totalDeposits - this.analytics.totalWithdrawals

      // Показатели эффективности
      this.analytics.completionRate = this.analytics.totalOrders > 0
        ? Math.round((this.analytics.completedOrders / this.analytics.totalOrders) * 100)
        : 0

      this.analytics.avgOrdersPerDay = parseFloat((this.analytics.totalOrders / daysDiff).toFixed(1))
      this.analytics.avgRevenuePerDay = this.analytics.totalRevenue / daysDiff

      // Лучший день
      this.analytics.bestDay = this.findBestDay(orders)

      // Антифрод статистика
      console.log('Данные поездок для антифрода:', orders.slice(0, 2))
      this.calculateAntifraudMetrics(orders)
    },

    // Поиск лучшего дня по количеству поездок и доходу
    findBestDay(orders) {
      if (orders.length === 0) return null

      const dayStats = {}

      orders.forEach(order => {
        const day = moment(order.created_at).format('DD.MM.YYYY')
        if (!dayStats[day]) {
          dayStats[day] = { date: day, orders: 0, revenue: 0 }
        }
        dayStats[day].orders++
        if (order.status === 'complete') {
          dayStats[day].revenue += parseFloat(order.cost) || 0
        }
      })

      // Находим день с максимальным доходом
      const bestDay = Object.values(dayStats).reduce((best, current) => {
        return current.revenue > best.revenue ? current : best
      }, Object.values(dayStats)[0])

      return bestDay
    },

    /**
     * Расчет антифрод метрик для выявления подозрительной активности водителя
     *
     * Антифрод показатели помогают выявить:
     * - Завышенные тарифы (максимальная стоимость)
     * - Затянутые поездки (максимальная продолжительность)
     * - Фиктивные короткие поездки (минимальная продолжительность)
     * - Накрутку заказов (максимальное количество поездок в час)
     *
     * @param {Array} orders - Массив поездок водителя
     */
    calculateAntifraudMetrics(orders) {
      // Если нет поездок, обнуляем все показатели
      if (orders.length === 0) {
        const emptyData = {
          maxCost: 0,
          maxDuration: 0,
          minDuration: 0,
          maxOrdersPerHour: 0,
          routeDeviations: 0,
          suspiciousStops: 0,
          avgRouteEfficiency: 0
        }
        this.analytics.antifraud = emptyData
        return emptyData
      }

      // Рассчитываем все антифрод показатели
      const antifraudData = {
        // Максимальная стоимость поездки - может указывать на завышенные тарифы или ошибки в расчете
        maxCost: Math.max(...orders.map(o => parseFloat(o.cost) || 0)),

        // Максимальная продолжительность - может указывать на затянутые поездки (возможно намеренно)
        maxDuration: this.calculateMaxDuration(orders),

        // Минимальная продолжительность - может указывать на короткие поездки (возможно фиктивные)
        minDuration: this.calculateMinDuration(orders),

        // Максимальное количество поездок в час - может указывать на накрутку заказов
        maxOrdersPerHour: this.calculateMaxOrdersPerHour(orders),

        // Анализ маршрутов (на основе кэшированных треков)
        ...this.analyzeRoutes(orders)
      }

      this.analytics.antifraud = antifraudData

      // Расчет метрик безопасности (если есть кэшированные треки)
      this.calculateSafetyMetrics(orders)

      return antifraudData
    },

    /**
     * Анализ маршрутов на предмет подозрительной активности
     *
     * @param {Array} orders - Массив поездок
     * @returns {Object} Метрики подозрительных маршрутов
     */
    analyzeRoutes(orders) {
      let routeDeviations = 0
      let suspiciousStops = 0
      let totalEfficiency = 0
      let ordersWithTracks = 0

      // Используем треки НАПРЯМУЮ из кэша (как в деталях нарушений)
      const cachedOrders = Object.entries(this.orderTracksCache).map(([orderId, cached]) => ({
        id: orderId,
        track: cached.track,
        orderData: orders.find(o => o.id === orderId) || {
          id: orderId,
          distance: cached.track && cached.track.length > 0 ? cached.track[cached.track.length - 1]?.distance : 0,
          number: `#${orderId.substring(0, 8)}`,
          created_at: new Date().toISOString(),
          route: {
            from: 'Неизвестно',
            to: 'Неизвестно'
          }
        }
      }))

      console.log('🔍 analyzeRoutes: треков в кэше:', cachedOrders.length)

      cachedOrders.forEach(cachedOrder => {
        if (cachedOrder.track && cachedOrder.track.length > 0) {
          ordersWithTracks++

          // Анализируем подозрительные остановки (используем тот же метод что и в деталях)
          const stops = this.findSuspiciousStopsInTrack(cachedOrder.track)
          suspiciousStops += stops.length

          // Анализируем отклонения маршрута (используем тот же метод что и в деталях)
          const deviations = this.findRouteDeviationsInTrack(cachedOrder.track, cachedOrder.orderData)
          routeDeviations += deviations.length

          // Рассчитываем эффективность для статистики
          const efficiency = this.calculateRouteEfficiency(cachedOrder.track, cachedOrder.orderData)
          totalEfficiency += efficiency

          if (deviations.length > 0) {
            console.log(`🚨 Подозрительный маршрут: ${deviations.length} отклонений для заказа ${cachedOrder.id}`)
          }
        }
      })

      console.log('✅ analyzeRoutes результат:', { routeDeviations, suspiciousStops, ordersWithTracks })

      const avgRouteEfficiency = ordersWithTracks > 0
        ? Math.round(totalEfficiency / ordersWithTracks)
        : 100 // Если нет данных, считаем идеальным

      return {
        routeDeviations,
        suspiciousStops,
        avgRouteEfficiency
      }
    },


    /**
     * Расчет эффективности маршрута
     *
     * Сравнивает реальное расстояние с расстоянием из заказа
     *
     * @param {Array} track - Массив точек трека
     * @param {Object} order - Объект заказа
     * @returns {number} Эффективность в процентах (0-100)
     */
    calculateRouteEfficiency(track, order) {
      console.log('🔍 calculateRouteEfficiency:', {
        trackLength: track?.length || 0,
        orderDistance: order?.distance,
        orderId: order?.id
      })

      if (!track || track.length === 0) {
        console.log('❌ Нет трека для расчёта эффективности')
        return 100
      }

      if (!order.distance) {
        console.log('⚠️ Нет данных о расстоянии в заказе, пропускаем расчёт эффективности')
        return 100
      }

      // Реальное расстояние из трека
      const realDistance = track[track.length - 1]?.distance || 0

      // Ожидаемое расстояние из заказа
      const expectedDistance = parseFloat(order.distance) || 0

      console.log('📊 Расстояния в calculateRouteEfficiency:', { realDistance, expectedDistance })

      if (expectedDistance === 0) {
        console.log('❌ Ожидаемое расстояние = 0')
        return 100
      }

      // Процент эффективности = ожидаемое / реальное * 100
      // Если реальное > ожидаемого, эффективность падает
      const efficiency = Math.min(100, (expectedDistance / realDistance) * 100)

      console.log('📈 Эффективность в calculateRouteEfficiency:', efficiency, '%')

      return Math.round(efficiency)
    },

    /**
     * Загрузка аналитики безопасности водителя
     *
     * Загружает ВСЕ поездки за период и анализирует их для расчета:
     * - Превышений скорости
     * - Рейтинга вождения
     * - Эффективности маршрутов
     * - Подозрительной активности
     */
    async loadSafetyAnalytics() {
      this.isLoadingAnalytics = true
      this.analyticsLoadingProgress.isLoading = true
      this.analyticsLoadingProgress.ordersLoaded = 0

      try {
        // Небольшая задержка перед началом загрузки
        await this.sleep(500)

        // Загружаем все поездки за период
        const ordersData = await this.loadAllOrdersForAnalytics()

        // Загружаем треки для выборки завершённых поездок
        await this.loadTracksForAnalytics(ordersData)

        // Вычисляем аналитику на основе всех загруженных данных
        this.calculateAnalytics(ordersData, [])

        const tracksCount = Object.keys(this.orderTracksCache).length
        this.$notify('Успех', `Загружено ${ordersData.length} поездок и ${tracksCount} треков`, 'success', 3000)
      } catch (error) {
        console.error('Error loading safety analytics:', error)
        this.$notify('Ошибка', 'Ошибка загрузки аналитики безопасности', 'error', 5000)
      } finally {
        this.isLoadingAnalytics = false
        this.analyticsLoadingProgress.isLoading = false
      }
    },

    /**
     * Умная загрузка треков для аналитики
     *
     * Стратегия:
     * 1. Загружаем треки только для завершённых поездок (status='complete')
     * 2. Ограничиваем количество (макс 20-30 треков) для избежания rate limiting
     * 3. Приоритет: последние поездки + случайная выборка из остальных
     * 4. Большие задержки между запросами (2-3 секунды)
     *
     * @param {Array} orders - Массив всех поездок
     */
    async loadTracksForAnalytics(orders) {
      // Фильтруем только завершённые поездки
      const completedOrders = orders.filter(o => o.status === 'complete')

      if (completedOrders.length === 0) {
        console.log('Нет завершённых поездок для загрузки треков')
        return
      }

      // Загружаем ВСЕ треки для полной картины
      console.log(`🔄 Загружаем треки для ВСЕХ ${completedOrders.length} завершённых поездок`)

      // Исключаем те, что уже в кэше
      const ordersNeedingTracks = completedOrders.filter(order => {
        const cached = this.orderTracksCache[order.id]
        if (!cached) return true

        // Проверяем TTL кэша (1 час)
        const CACHE_TTL = 60 * 60 * 1000
        return Date.now() - cached.loadedAt > CACHE_TTL
      })

      if (ordersNeedingTracks.length === 0) {
        console.log('✅ Все треки уже в кэше')
        return
      }

      // Загружаем ВСЕ треки без ограничений
      const ordersToLoadTracks = ordersNeedingTracks

      console.log(`Загрузка ${ordersToLoadTracks.length} треков из ${completedOrders.length} завершённых поездок`)

      // Устанавливаем общее количество для прогресс-бара
      this.analyticsLoadingProgress.tracksTotal = ordersToLoadTracks.length
      this.analyticsLoadingProgress.tracksLoaded = 0

      // Загружаем треки последовательно по одному
      let loadedCount = 0
      let failedCount = 0

      for (const order of ordersToLoadTracks) {
        try {
          // Загружаем трек
          await this.loadOrderTrackForAnalytics(order.id)

          loadedCount++

          // Обновляем прогресс и аналитику в реальном времени
          this.analyticsLoadingProgress.tracksLoaded = loadedCount
          this.updateAnalyticsInRealTime()

          // Небольшая задержка между запросами для избежания rate limiting
          await this.sleep(1000)

        } catch (error) {
          console.error(`Failed to load track for order ${order.id}:`, error)
          failedCount++

          // Если много ошибок подряд - прерываем загрузку
          if (failedCount > 5) {
            console.log('❌ Слишком много ошибок, прерываем загрузку треков')
            this.$notify('Предупреждение', 'Прервана загрузка треков из-за ошибок', 'warning', 3000)
            break
          }

          // Увеличенная задержка после ошибки
          await this.sleep(5000)
        }
      }

      console.log(`Загружено треков: ${loadedCount}, ошибок: ${failedCount}`)

      // Сбрасываем прогресс треков
      this.analyticsLoadingProgress.tracksLoaded = 0
      this.analyticsLoadingProgress.tracksTotal = 0
    },

    /**
     * Загрузка трека для аналитики (без UI уведомлений)
     *
     * @param {String} orderId - ID заказа
     */
    async loadOrderTrackForAnalytics(orderId) {
      // Проверяем кэш
      const CACHE_TTL = 60 * 60 * 1000
      if (this.orderTracksCache[orderId]) {
        const cached = this.orderTracksCache[orderId]
        if (Date.now() - cached.loadedAt < CACHE_TTL) {
          return // Используем кэшированный трек
        }
      }

      // Загружаем из API
      const response = await this.axios.get(
        `/api/v1/taxi/orders/${orderId}/track`,
        {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        }
      )

      if (response.data.success && response.data.data.track) {
        const track = response.data.data.track
        const stats = this.calculateTrackStats(track)

        // Сохраняем в кэш
        this.orderTracksCache[orderId] = {
          track: track,
          stats: stats,
          loadedAt: Date.now()
        }
      } else {
        throw new Error(response.data.error || 'Failed to load track')
      }
    },

    /**
     * Получить случайную выборку из массива
     *
     * @param {Array} array - Исходный массив
     * @param {Number} count - Количество элементов для выборки
     * @returns {Array} Случайная выборка
     */
    getRandomSample(array, count) {
      const shuffled = [...array].sort(() => 0.5 - Math.random())
      return shuffled.slice(0, Math.min(count, array.length))
    },

    /**
     * Показать детали нарушения
     *
     * @param {String} violationType - Тип нарушения
     */
    showViolationDetails(violationType) {
      console.log('🔍 Показываем детали нарушения:', violationType)

      this.selectedViolationType = violationType
      this.violationDetails = this.getViolationDetails(violationType)

      console.log('📝 Найдено деталей:', this.violationDetails.length)

      this.violationDetailsDialog = true
    },

    /**
     * Получить детальную информацию о нарушениях
     *
     * @param {String} violationType - Тип нарушения
     * @returns {Array} Детали нарушений
     */
    getViolationDetails(violationType) {
      const details = []

      // Получаем треки НАПРЯМУЮ из кэша (не полагаемся на this.orders)
      const cachedOrders = Object.entries(this.orderTracksCache).map(([orderId, cached]) => ({
        id: orderId,
        track: cached.track,
        stats: cached.stats,
        // Пытаемся найти заказ в списке, если есть
        orderData: this.orders.find(o => o.id === orderId) || {
          id: orderId,
          distance: cached.track && cached.track.length > 0 ? cached.track[cached.track.length - 1]?.distance : 0,
          number: `#${orderId.substring(0, 8)}`,
          created_at: new Date().toISOString(),
          route: {
            from: 'Неизвестно',
            to: 'Неизвестно'
          }
        }
      }))

      console.log(`📊 Анализ ${violationType}: треков в кэше - ${cachedOrders.length}`)
      console.log('🔍 Кэш треков:', Object.keys(this.orderTracksCache))
      console.log('📋 Все заказы:', this.orders.length)

      // Если треков нет в кэше вообще
      if (cachedOrders.length === 0) {
        console.log('⚠️ Нет данных для анализа нарушений')
        return []
      }

      switch (violationType) {
        case 'speed':
          // Анализируем превышения скорости
          cachedOrders.forEach(cachedOrder => {
            if (!cachedOrder.track || cachedOrder.track.length === 0) return

            const track = cachedOrder.track
            const speedViolations = this.findSpeedViolationsInTrack(track)

            speedViolations.forEach(violation => {
              details.push({
                orderId: cachedOrder.id,
                orderNumber: cachedOrder.orderData.number || `#${cachedOrder.id.substring(0, 8)}`,
                date: this.formatDateForDisplay(cachedOrder.orderData.created_at || new Date()),
                time: this.formatTime(violation.tracked_at),
                speed: Math.round(violation.speed * 3.6), // м/с -> км/ч
                limit: violation.speedLimit || 60,
                location: violation.location,
                duration: violation.duration || 'Неизвестно',
                severity: this.getSpeedViolationSeverity(violation.speed, violation.speedLimit)
              })
            })
          })
          console.log('✅ Найдено превышений скорости:', details.length)
          break

        case 'route':
          // Анализируем отклонения маршрутов
          console.log('🗺️ Анализируем отклонения маршрутов в деталях...')
          cachedOrders.forEach(cachedOrder => {
            if (!cachedOrder.track || cachedOrder.track.length === 0) {
              console.log(`❌ Нет трека для заказа ${cachedOrder.id}`)
              return
            }

            console.log(`🔍 Анализируем заказ ${cachedOrder.id}:`, {
              trackLength: cachedOrder.track.length,
              orderData: cachedOrder.orderData
            })

            const track = cachedOrder.track
            const routeDeviations = this.findRouteDeviationsInTrack(track, cachedOrder.orderData)

            console.log(`📊 Найдено отклонений для заказа ${cachedOrder.id}:`, routeDeviations.length)
            console.log('🔍 Детали отклонений:', routeDeviations)

            routeDeviations.forEach(deviation => {
              console.log('📝 Добавляем отклонение:', deviation)
              details.push({
                orderId: cachedOrder.id,
                orderNumber: cachedOrder.orderData.number || `#${cachedOrder.id.substring(0, 8)}`,
                date: this.formatDateForDisplay(cachedOrder.orderData.created_at || new Date()),
                from: cachedOrder.orderData.route?.from || 'Неизвестно',
                to: cachedOrder.orderData.route?.to || 'Неизвестно',
                deviation: Math.round(deviation.deviation),
                expectedDistance: Math.round(deviation.expectedDistance),
                actualDistance: Math.round(deviation.actualDistance),
                efficiency: Math.round(deviation.efficiency),
                severity: this.getRouteDeviationSeverity(deviation.deviation)
              })
            })
          })
          console.log('✅ Найдено отклонений маршрутов:', details.length)
          console.log('📋 Все детали отклонений:', details)
          break

        case 'stops':
          // Анализируем подозрительные остановки
          cachedOrders.forEach(cachedOrder => {
            if (!cachedOrder.track || cachedOrder.track.length === 0) return

            const track = cachedOrder.track
            const suspiciousStops = this.findSuspiciousStopsInTrack(track)

            suspiciousStops.forEach(stop => {
              details.push({
                orderId: cachedOrder.id,
                orderNumber: cachedOrder.orderData.number || `#${cachedOrder.id.substring(0, 8)}`,
                date: this.formatDateForDisplay(cachedOrder.orderData.created_at || new Date()),
                startTime: this.formatTime(stop.startTime),
                endTime: this.formatTime(stop.endTime),
                duration: this.formatDurationFromSeconds(stop.duration),
                location: stop.location,
                reason: stop.reason || 'Необъяснимая остановка',
                severity: this.getStopSeverity(stop.duration, stop.reason)
              })
            })
          })
          console.log('✅ Найдено подозрительных остановок:', details.length)
          break
      }

      // Сортируем по дате (новые сверху)
      return details.sort((a, b) => new Date(b.date + ' ' + (b.time || b.startTime)) - new Date(a.date + ' ' + (a.time || a.startTime)))
    },

    /**
     * Найти превышения скорости в треке
     *
     * @param {Array} track - Трек поездки
     * @returns {Array} Массив превышений скорости
     */
    findSpeedViolationsInTrack(track) {
      const violations = []
      const SPEED_LIMIT = 60 // км/ч (можно настроить)
      const SPEED_LIMIT_MS = SPEED_LIMIT / 3.6 // м/с

      for (let i = 0; i < track.length; i++) {
        const point = track[i]
        if (point.speed && point.speed > SPEED_LIMIT_MS) {
          // Группируем соседние превышения
          const violation = {
            tracked_at: point.tracked_at,
            speed: point.speed,
            speedLimit: SPEED_LIMIT,
            location: point.location,
            duration: 0,
            points: [point]
          }

          // Ищем соседние точки превышения
          let j = i + 1
          while (j < track.length && track[j].speed && track[j].speed > SPEED_LIMIT_MS) {
            violation.points.push(track[j])
            j++
          }

          // Рассчитываем продолжительность превышения
          if (violation.points.length > 1) {
            const startTime = new Date(violation.points[0].tracked_at)
            const endTime = new Date(violation.points[violation.points.length - 1].tracked_at)
            violation.duration = Math.round((endTime - startTime) / 1000) // секунды
          }

          violations.push(violation)
          i = j - 1 // Пропускаем уже обработанные точки
        }
      }

      return violations
    },

    /**
     * Найти отклонения маршрута в треке
     *
     * @param {Array} track - Трек поездки
     * @param {Object} order - Данные заказа
     * @returns {Array} Массив отклонений маршрута
     */
    findRouteDeviationsInTrack(track, order) {
      console.log('🔍 findRouteDeviationsInTrack:', {
        trackLength: track?.length || 0,
        orderDistance: order?.distance,
        orderId: order?.id
      })

      if (!track || track.length === 0) {
        console.log('❌ Нет трека')
        return []
      }

      // Получаем реальное расстояние из трека
      const realDistance = track[track.length - 1]?.distance || 0

      // Если нет данных о заказе, используем только реальное расстояние
      if (!order || !order.distance) {
        console.log('⚠️ Нет данных о заказе, используем только реальное расстояние')
        // Возвращаем отклонение если реальное расстояние слишком большое
        if (realDistance > 10000) { // Больше 10км - подозрительно
          return [{
            deviation: realDistance - 5000, // Считаем отклонением от 5км
            expectedDistance: 5000,
            actualDistance: realDistance,
            efficiency: 50
          }]
        }
        return []
      }

      const expectedDistance = parseFloat(order.distance) || 0
      console.log('📊 Расстояния:', { realDistance, expectedDistance })

      if (expectedDistance === 0) {
        console.log('❌ Ожидаемое расстояние = 0')
        return []
      }

      const deviation = Math.abs(realDistance - expectedDistance)
      const efficiency = Math.min(100, (expectedDistance / realDistance) * 100)

      console.log('📈 Эффективность:', efficiency, '%')
      console.log('🔍 Критерии отклонения: эффективность < 80% (было 70%)')

      // Возвращаем отклонение если эффективность < 80% (подозрительный маршрут)
      if (efficiency < 80) {
        console.log('🚨 Подозрительный маршрут в деталях! Эффективность:', efficiency, '%')
        return [{
          deviation: deviation,
          expectedDistance: expectedDistance,
          actualDistance: realDistance,
          efficiency: efficiency
        }]
      }

      console.log('✅ Маршрут нормальный, эффективность:', efficiency, '%')
      return []
    },

    /**
     * Найти подозрительные остановки в треке
     *
     * @param {Array} track - Трек поездки
     * @returns {Array} Массив подозрительных остановок
     */
    findSuspiciousStopsInTrack(track) {
      const stops = []
      const MIN_STOP_DURATION = 300 // 5 минут в секундах
      const MAX_SPEED_FOR_STOP = 2 // м/с (7.2 км/ч)

      let currentStop = null

      for (let i = 0; i < track.length; i++) {
        const point = track[i]

        if (point.speed <= MAX_SPEED_FOR_STOP) {
          // Низкая скорость - возможная остановка
          if (!currentStop) {
            currentStop = {
              startTime: point.tracked_at,
              startIndex: i,
              location: point.location,
              points: [point]
            }
          } else {
            currentStop.points.push(point)
          }
        } else {
          // Скорость нормальная - завершаем остановку
          if (currentStop) {
            const stopDuration = this.calculateStopDuration(currentStop)
            if (stopDuration >= MIN_STOP_DURATION) {
              stops.push({
                startTime: currentStop.startTime,
                endTime: point.tracked_at,
                duration: stopDuration,
                location: currentStop.location,
                reason: this.analyzeStopReason(currentStop, track)
              })
            }
            currentStop = null
          }
        }
      }

      return stops
    },

    /**
     * Рассчитать продолжительность остановки
     *
     * @param {Object} stop - Данные остановки
     * @returns {Number} Продолжительность в секундах
     */
    calculateStopDuration(stop) {
      if (stop.points.length < 2) return 0

      const startTime = new Date(stop.points[0].tracked_at)
      const endTime = new Date(stop.points[stop.points.length - 1].tracked_at)

      return Math.round((endTime - startTime) / 1000)
    },

    /**
     * Анализировать причину остановки
     *
     * @param {Object} stop - Данные остановки
     * @param {Array} track - Полный трек
     * @returns {String} Причина остановки
     */
    analyzeStopReason(stop, track) {
      // Простая эвристика для определения причины
      const duration = this.calculateStopDuration(stop)

      if (duration > 1800) { // > 30 минут
        return 'Длительная остановка (возможно обед/отдых)'
      } else if (duration > 600) { // > 10 минут
        return 'Средняя остановка (возможно заправка/посещение)'
      } else {
        return 'Краткая остановка (возможно светофор/пробка)'
      }
    },

    /**
     * Получить серьезность превышения скорости
     *
     * @param {Number} speed - Скорость в м/с
     * @param {Number} limit - Лимит скорости в км/ч
     * @returns {String} Уровень серьезности
     */
    getSpeedViolationSeverity(speed, limit) {
      const speedKmh = speed * 3.6
      const excess = speedKmh - limit

      if (excess > 30) return 'Критично'
      if (excess > 20) return 'Высокий'
      if (excess > 10) return 'Средний'
      return 'Низкий'
    },

    /**
     * Получить серьезность отклонения маршрута
     *
     * @param {Number} deviation - Отклонение в метрах
     * @returns {String} Уровень серьезности
     */
    getRouteDeviationSeverity(deviation) {
      if (deviation > 5000) return 'Критично'
      if (deviation > 2000) return 'Высокий'
      if (deviation > 1000) return 'Средний'
      return 'Низкий'
    },

    /**
     * Получить серьезность остановки
     *
     * @param {Number} duration - Продолжительность в секундах
     * @param {String} reason - Причина остановки
     * @returns {String} Уровень серьезности
     */
    getStopSeverity(duration, reason) {
      if (duration > 1800) return 'Критично' // > 30 минут
      if (duration > 600) return 'Высокий'   // > 10 минут
      if (duration > 300) return 'Средний'  // > 5 минут
      return 'Низкий'
    },

    /**
     * Форматировать дату для отображения
     *
     * @param {String} dateTime - Дата и время
     * @returns {String} Отформатированная дата
     */
    formatDateForDisplay(dateTime) {
      if (!dateTime) return 'Неизвестно'
      return moment(dateTime).format('DD.MM.YYYY')
    },

    /**
     * Форматировать время
     *
     * @param {String} dateTime - Дата и время
     * @returns {String} Отформатированное время
     */
    formatTime(dateTime) {
      if (!dateTime) return 'Неизвестно'
      return new Date(dateTime).toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    /**
     * Форматировать продолжительность из секунд
     *
     * @param {Number} seconds - Секунды
     * @returns {String} Отформатированная продолжительность
     */
    formatDurationFromSeconds(seconds) {
      if (!seconds || seconds === 0) return '0 сек'
      if (seconds < 60) return `${Math.round(seconds)} сек`
      if (seconds < 3600) return `${Math.round(seconds / 60)} мин`

      const hours = Math.floor(seconds / 3600)
      const minutes = Math.round((seconds % 3600) / 60)

      if (minutes === 0) {
        return `${hours} ч`
      } else {
        return `${hours} ч ${minutes} мин`
      }
    },

    /**
     * Получить иконку для типа нарушения
     *
     * @param {String} violationType - Тип нарушения
     * @returns {String} Название иконки
     */
    getViolationIcon(violationType) {
      switch (violationType) {
        case 'speed': return 'mdi-speedometer'
        case 'route': return 'mdi-map-marker-path'
        case 'stops': return 'mdi-pause-circle'
        default: return 'mdi-alert'
      }
    },

    /**
     * Получить цвет иконки для типа нарушения
     *
     * @param {String} violationType - Тип нарушения
     * @returns {String} Цвет
     */
    getViolationIconColor(violationType) {
      switch (violationType) {
        case 'speed': return 'error'
        case 'route': return 'warning'
        case 'stops': return 'info'
        default: return 'grey'
      }
    },

    /**
     * Получить заголовок для типа нарушения
     *
     * @param {String} violationType - Тип нарушения
     * @returns {String} Заголовок
     */
    getViolationTitle(violationType) {
      switch (violationType) {
        case 'speed': return 'Детали превышений скорости'
        case 'route': return 'Детали отклонений маршрутов'
        case 'stops': return 'Детали подозрительных остановок'
        default: return 'Детали нарушений'
      }
    },

    /**
     * Получить текст количества для типа нарушения
     *
     * @param {String} violationType - Тип нарушения
     * @returns {String} Текст количества
     */
    getViolationCountText(violationType) {
      switch (violationType) {
        case 'speed': return 'превышений скорости'
        case 'route': return 'отклонений маршрутов'
        case 'stops': return 'подозрительных остановок'
        default: return 'нарушений'
      }
    },

    /**
     * Получить цвет для скорости
     *
     * @param {Number} speed - Скорость
     * @param {Number} limit - Лимит
     * @returns {String} CSS класс
     */
    getSpeedColor(speed, limit) {
      const excess = speed - limit
      if (excess > 30) return 'error--text'
      if (excess > 20) return 'deep-orange--text'
      if (excess > 10) return 'orange--text'
      return 'warning--text'
    },

    /**
     * Получить цвет для эффективности
     *
     * @param {Number} efficiency - Эффективность в %
     * @returns {String} CSS класс
     */
    getEfficiencyColor(efficiency) {
      if (efficiency >= 90) return 'success--text'
      if (efficiency >= 80) return 'light-green--text'
      if (efficiency >= 70) return 'warning--text'
      return 'error--text'
    },

    /**
     * Получить цвет для продолжительности
     *
     * @param {String} duration - Продолжительность
     * @returns {String} CSS класс
     */
    getDurationColor(duration) {
      if (duration.includes('ч')) return 'error--text'
      if (duration.includes('мин') && parseInt(duration) > 10) return 'warning--text'
      return 'info--text'
    },

    /**
     * Получить цвет для серьезности
     *
     * @param {String} severity - Уровень серьезности
     * @returns {String} Цвет
     */
    getSeverityColor(severity) {
      switch (severity) {
        case 'Критично': return 'error'
        case 'Высокий': return 'deep-orange'
        case 'Средний': return 'warning'
        case 'Низкий': return 'info'
        default: return 'grey'
      }
    },

    /**
     * Получить цвет текста для серьезности
     *
     * @param {String} severity - Уровень серьезности
     * @returns {String} Цвет текста
     */
    getSeverityTextColor(severity) {
      switch (severity) {
        case 'Критично': return 'white'
        case 'Высокий': return 'white'
        case 'Средний': return 'white'
        case 'Низкий': return 'white'
        default: return 'white'
      }
    },


    /**
     * Расчет метрик безопасности на основе данных поездок
     *
     * Анализирует:
     * - Скорости из кэшированных треков
     * - Общее расстояние
     * - Рейтинг вождения (комплексная оценка)
     *
     * @param {Array} orders - Массив поездок
     */
    calculateSafetyMetrics(orders) {
      // Используем треки НАПРЯМУЮ из кэша (как в деталях нарушений)
      const cachedOrders = Object.entries(this.orderTracksCache).map(([orderId, cached]) => ({
        id: orderId,
        track: cached.track,
        orderData: orders.find(o => o.id === orderId) || {}
      }))

      if (cachedOrders.length === 0) {
        this.analytics.safety = {
          speedViolations: 0,
          avgSpeed: 0,
          drivingScore: 0,
          totalDistance: 0
        }
        return
      }

      let totalDistance = 0
      let speedViolations = 0
      let allSpeeds = []
      let ordersWithTracks = 0

      // Анализируем каждую поездку с треком
      cachedOrders.forEach(cachedOrder => {
        // Если есть трек - анализируем его
        if (cachedOrder.track && cachedOrder.track.length > 0) {
          ordersWithTracks++

          // Суммируем РЕАЛЬНЫЕ расстояния из треков (в метрах)
          const realDistance = cachedOrder.track[cachedOrder.track.length - 1]?.distance || 0
          totalDistance += realDistance

          console.log(`📊 Заказ ${cachedOrder.id}: реальное расстояние ${realDistance}м, из заказа ${cachedOrder.orderData.distance || 0}м`)

          // Анализируем скорости в треке
          cachedOrder.track.forEach(point => {
            if (point.speed) {
              const speedKmh = point.speed * 3.6 // м/с -> км/ч
              allSpeeds.push(speedKmh)

              // Порог превышения скорости (может зависеть от города/типа дороги)
              const SPEED_LIMIT = 80 // км/ч (можно сделать настраиваемым)
              if (speedKmh > SPEED_LIMIT) {
                speedViolations++
              }
            }
          })
        }
      })

      // Средняя скорость
      const avgSpeed = allSpeeds.length > 0
        ? Math.round(allSpeeds.reduce((a, b) => a + b, 0) / allSpeeds.length)
        : 0

      // Рейтинг вождения (0-100)
      const drivingScore = this.calculateDrivingScore({
        speedViolations,
        completionRate: this.analytics.completionRate,
        ordersWithTracks,
        totalOrders: orders.length
      })

      // Конвертируем метры в километры
      const totalDistanceKm = totalDistance / 1000

      console.log(`📊 Итого пробег: ${totalDistance}м = ${totalDistanceKm.toFixed(1)}км`)

      // Обновляем метрики безопасности
      this.analytics.safety = {
        speedViolations: speedViolations,
        avgSpeed: avgSpeed,
        drivingScore: drivingScore,
        totalDistance: totalDistanceKm
      }
    },

    /**
     * Расчет рейтинга вождения водителя (0-100)
     *
     * Комплексная оценка на основе:
     * - Процент завершенных поездок (40%)
     * - Отсутствие превышений скорости (30%)
     * - Доступность данных треков (20%)
     * - Другие факторы (10%)
     *
     * @param {Object} params - Параметры для расчета
     * @returns {number} Рейтинг от 0 до 100
     */
    calculateDrivingScore({ speedViolations, completionRate, ordersWithTracks, totalOrders }) {
      let score = 0

      // 1. Процент завершенных поездок (макс 40 баллов)
      score += (completionRate / 100) * 40

      // 2. Отсутствие превышений скорости (макс 30 баллов)
      // Чем меньше превышений, тем лучше
      const violationsPerOrder = totalOrders > 0 ? speedViolations / totalOrders : 0
      if (violationsPerOrder === 0) {
        score += 30
      } else if (violationsPerOrder < 1) {
        score += 25
      } else if (violationsPerOrder < 3) {
        score += 15
      } else if (violationsPerOrder < 5) {
        score += 5
      }
      // Если > 5 превышений на заказ - 0 баллов

      // 3. Наличие данных треков (макс 20 баллов)
      const tracksPercentage = totalOrders > 0 ? (ordersWithTracks / totalOrders) * 100 : 0
      score += (tracksPercentage / 100) * 20

      // 4. Бонусные баллы (макс 10 баллов)
      // Можно добавить дополнительные критерии в будущем
      score += 10

      return Math.round(Math.min(score, 100))
    },

    /**
     * Расчет максимальной продолжительности поездки в минутах
     *
     * Анализирует все поездки и находит самую длинную по времени.
     * Продолжительность = ended_at - started_at
     *
     * @param {Array} orders - Массив поездок водителя
     * @returns {number} Максимальная продолжительность в минутах
     */
    calculateMaxDuration(orders) {
      let maxDuration = 0
      let validOrdersCount = 0

      // Проходим по всем поездкам и ищем максимальную продолжительность
      orders.forEach(order => {
        // Проверяем, что у поездки есть время начала и окончания
        if (order.started_at && order.ended_at) {
          const start = moment(order.started_at)
          const end = moment(order.ended_at)
          const duration = end.diff(start, 'minutes')

          validOrdersCount++

          // Обновляем максимум, если текущая поездка длиннее
          if (duration > maxDuration) {
            maxDuration = duration
          }
        }
      })

      // Отладочная информация
      console.log(`Максимальная продолжительность: ${maxDuration} мин из ${validOrdersCount} поездок с временными метками`)
      console.log('Примеры поездок с временными метками:', orders.slice(0, 3).map(o => ({
        id: o.id,
        status: o.status,
        started_at: o.started_at,
        ended_at: o.ended_at
      })))

      return maxDuration
    },

    /**
     * Расчет минимальной продолжительности завершенной поездки в минутах
     *
     * Анализирует только завершенные поездки и находит самую короткую.
     * Исключает отмененные поездки, так как они могут иметь некорректные временные метки.
     *
     * @param {Array} orders - Массив поездок водителя
     * @returns {number} Минимальная продолжительность в минутах
     */
    calculateMinDuration(orders) {
      // Фильтруем только завершенные поездки с корректными временными метками
      const completedOrders = orders.filter(o =>
        o.status === 'complete' &&
        o.started_at &&
        o.ended_at
      )

      // Отладочная информация
      console.log(`Завершенных поездок с временными метками: ${completedOrders.length} из ${orders.length}`)
      console.log('Статусы поездок:', orders.reduce((acc, o) => {
        acc[o.status] = (acc[o.status] || 0) + 1
        return acc
      }, {}))

      // Если нет завершенных поездок, возвращаем 0
      if (completedOrders.length === 0) return 0

      let minDuration = Infinity

      // Проходим по завершенным поездкам и ищем минимальную продолжительность
      completedOrders.forEach(order => {
        const start = moment(order.started_at)
        const end = moment(order.ended_at)
        const duration = end.diff(start, 'minutes')

        // Обновляем минимум, если текущая поездка короче
        if (duration < minDuration) {
          minDuration = duration
        }
      })

      // Отладочная информация
      console.log(`Минимальная продолжительность: ${minDuration === Infinity ? 0 : minDuration} мин`)

      // Если минимум не найден (не должно происходить), возвращаем 0
      return minDuration === Infinity ? 0 : minDuration
    },

    /**
     * Расчет максимального количества поездок в час
     *
     * Группирует поездки по часам и находит час с максимальным количеством поездок.
     * Помогает выявить подозрительную активность - накрутку заказов в короткий период времени.
     *
     * @param {Array} orders - Массив поездок водителя
     * @returns {number} Максимальное количество поездок в час
     */
    calculateMaxOrdersPerHour(orders) {
      const hourlyStats = {}

      // Группируем поездки по часам
      orders.forEach(order => {
        if (order.created_at) {
          // Округляем время создания поездки до начала часа для группировки
          // Например: 14:23:45 -> 14:00:00
          const hour = moment(order.created_at).startOf('hour').format('YYYY-MM-DD HH:mm')

          // Увеличиваем счетчик поездок для данного часа
          if (!hourlyStats[hour]) {
            hourlyStats[hour] = 0
          }
          hourlyStats[hour]++
        }
      })

      // Находим максимальное количество поездок среди всех часов
      const maxOrdersPerHour = Object.values(hourlyStats).length > 0
        ? Math.max(...Object.values(hourlyStats))
        : 0

      return maxOrdersPerHour
    },

    // Вспомогательные методы для заголовков
    getTabIcon() {
      switch (this.activeTab) {
        case 0: return 'mdi-taxi'
        case 1: return 'mdi-credit-card-multiple'
        case 2: return 'mdi-chart-line'
        case 3: return 'mdi-shield-car'
        case 4: return 'mdi-cog'
        default: return 'mdi-taxi'
      }
    },

    getTabTitle() {
      switch (this.activeTab) {
        case 0: return 'Поездки водителя'
        case 1: return 'Транзакции водителя'
        case 2: return 'Аналитика водителя'
        case 3: return 'Безопасность водителя'
        case 4: return 'Управление водителем'
        default: return 'Поездки водителя'
      }
    },

    /**
     * Получить цвет для рейтинга вождения
     */
    getDrivingScoreColor() {
      const score = this.analytics.safety.drivingScore
      if (score >= 90) {
        return this.$vuetify.theme.dark ? 'green darken-4' : 'green lighten-4'
      } else if (score >= 70) {
        return this.$vuetify.theme.dark ? 'yellow darken-4' : 'yellow lighten-4'
      } else if (score >= 50) {
        return this.$vuetify.theme.dark ? 'orange darken-4' : 'orange lighten-4'
      } else {
        return this.$vuetify.theme.dark ? 'red darken-4' : 'red lighten-4'
      }
    },

    /**
     * Получить цвет прогресс-бара для рейтинга вождения
     */
    getDrivingScoreProgressColor() {
      const score = this.analytics.safety.drivingScore
      if (score >= 90) return 'success'
      else if (score >= 70) return 'warning'
      else if (score >= 50) return 'orange'
      else return 'error'
    },

    /**
     * Получить текстовую оценку рейтинга вождения
     */
    getDrivingScoreLabel() {
      const score = this.analytics.safety.drivingScore
      if (score >= 90) return 'Отлично'
      else if (score >= 70) return 'Хорошо'
      else if (score >= 50) return 'Удовлетворительно'
      else return 'Требуется улучшение'
    },

    /**
     * Получить рекомендации по безопасности на основе метрик
     */
    getSafetyRecommendations() {
      const recommendations = []

      // Проверяем превышения скорости
      if (this.analytics.safety.speedViolations > 10) {
        recommendations.push({
          title: 'Снизьте скорость',
          description: `Обнаружено ${this.analytics.safety.speedViolations} превышений скорости. Соблюдайте скоростной режим для безопасности и экономии топлива.`,
          icon: 'mdi-speedometer-slow',
          priority: 'high'
        })
      }

      // Проверяем эффективность маршрутов
      if (this.analytics.antifraud.avgRouteEfficiency < 80) {
        recommendations.push({
          title: 'Оптимизируйте маршруты',
          description: `Средняя эффективность маршрутов ${this.analytics.antifraud.avgRouteEfficiency}%. Используйте навигатор для выбора оптимального пути.`,
          icon: 'mdi-map-marker-path',
          priority: 'medium'
        })
      }

      // Проверяем подозрительные остановки
      if (this.analytics.antifraud.suspiciousStops > 5) {
        recommendations.push({
          title: 'Избегайте лишних остановок',
          description: `Обнаружено ${this.analytics.antifraud.suspiciousStops} подозрительных остановок. Минимизируйте остановки в пути для повышения эффективности.`,
          icon: 'mdi-car-brake-alert',
          priority: 'medium'
        })
      }

      // Проверяем процент завершенных поездок
      if (this.analytics.completionRate < 90) {
        recommendations.push({
          title: 'Повысьте процент завершенных поездок',
          description: `Процент завершенных поездок ${this.analytics.completionRate}%. Старайтесь завершать все принятые заказы.`,
          icon: 'mdi-checkbox-marked-circle-outline',
          priority: 'high'
        })
      }

      return recommendations
    },

    /**
     * Обновление аналитики в реальном времени по мере загрузки треков
     */
    updateAnalyticsInRealTime() {
      // Пересчитываем аналитику с уже загруженными треками
      const cachedOrders = Object.entries(this.orderTracksCache).map(([orderId, cached]) => ({
        id: orderId,
        track: cached.track,
        orderData: this.orders.find(o => o.id === orderId) || {}
      }))

      if (cachedOrders.length === 0) return

      try {
        // Сохраняем старые значения для сравнения
        const oldAntifraud = { ...this.analytics.antifraud }
        const oldSafety = { ...this.analytics.safety }

        // Обновляем все метрики
        this.calculateAntifraudMetrics(this.orders)
        this.calculateSafetyMetrics(this.orders) // Обновляем метрики безопасности отдельно

        // Логируем изменения
        console.log(`📊 Обновлена аналитика: ${cachedOrders.length} треков загружено`)
        console.log('🔍 Антифрод:', {
          routeDeviations: `${oldAntifraud.routeDeviations} → ${this.analytics.antifraud.routeDeviations}`,
          suspiciousStops: `${oldAntifraud.suspiciousStops} → ${this.analytics.antifraud.suspiciousStops}`,
          avgRouteEfficiency: `${oldAntifraud.avgRouteEfficiency} → ${this.analytics.antifraud.avgRouteEfficiency}`
        })
        console.log('🛡️ Безопасность:', {
          speedViolations: `${oldSafety.speedViolations} → ${this.analytics.safety.speedViolations}`,
          avgSpeed: `${oldSafety.avgSpeed} → ${this.analytics.safety.avgSpeed} км/ч`,
          drivingScore: `${oldSafety.drivingScore} → ${this.analytics.safety.drivingScore}`,
          totalDistance: `${oldSafety.totalDistance} → ${this.analytics.safety.totalDistance} км`
        })

        // Дополнительная отладка для вкладки Безопасность
        console.log('🔍 Детали безопасности:', {
          ordersWithTracks: cachedOrders.filter(o => o.track && o.track.length > 0).length,
          totalOrders: this.orders.length,
          cacheSize: Object.keys(this.orderTracksCache).length
        })

        // Принудительно обновляем Vue реактивность
        this.$forceUpdate()
      } catch (error) {
        console.error('Ошибка при обновлении аналитики в реальном времени:', error)
      }
    },

    /**
     * Форматирование расстояния (уже в километрах)
     */
    formatDistance(km) {
      if (!km || km === 0) return '0 км'
      if (km >= 1000) {
        return `${(km / 1000).toFixed(1)}тыс км`
      } else if (km >= 100) {
        return `${Math.round(km)} км`
      } else {
        return `${km.toFixed(1)} км`
      }
    },

    // Обработчики событий
    onPeriodChange() {
      if (this.selectedPeriod === 'custom') {
        // Устанавливаем период по умолчанию для кастомного
        this.customStartDate = moment().subtract(1, 'week').format('YYYY-MM-DD')
        this.customEndDate = moment().format('YYYY-MM-DD')
      }

      // При смене периода всегда перезагружаем данные
      if (this.client?.yandex_driver_id) {
        if (this.activeTab === 0) {
          this.loadOrders()
        } else if (this.activeTab === 1) {
          this.loadTransactions()
        } else if (this.activeTab === 2) {
          this.loadAnalytics()
        } else if (this.activeTab === 3) {
          this.loadSafetyAnalytics()
        }
      }
    },

    onCustomDateChange() {
      // При смене кастомных дат всегда перезагружаем данные
      if (this.customStartDate && this.customEndDate && this.client?.yandex_driver_id) {
        if (this.activeTab === 0) {
          this.loadOrders()
        } else if (this.activeTab === 1) {
          this.loadTransactions()
        } else if (this.activeTab === 2) {
          this.loadAnalytics()
        } else if (this.activeTab === 3) {
          this.loadSafetyAnalytics()
        }
      }
    },

    // Показать детали заказа
    async showOrderDetails(order) {
      this.selectedOrder = order
      this.orderDetailsDialog = true
      this.orderTransactions = []
      this.selectedOrderTrack = []

      // Загружаем транзакции для этого заказа
      await this.loadOrderTransactions(order.id)

      // Автоматически загружаем трек для завершенных поездок если есть в кэше
      if (order.status === 'complete' && this.orderTracksCache[order.id]) {
        const cached = this.orderTracksCache[order.id]
        this.selectedOrderTrack = cached.track
        this.trackStats = cached.stats
      }
    },

    /**
     * Загрузка трека заказа из API Yandex
     *
     * Использует кэширование для оптимизации:
     * - Сохраняет загруженные треки в памяти
     * - TTL кэша: 1 час (треки старых заказов не меняются)
     */
    async loadOrderTrack(orderId) {
      // Проверяем кэш (TTL: 1 час)
      const CACHE_TTL = 60 * 60 * 1000 // 1 час
      if (this.orderTracksCache[orderId]) {
        const cached = this.orderTracksCache[orderId]
        const isValid = Date.now() - cached.loadedAt < CACHE_TTL

        if (isValid) {
          console.log('Использование кэшированного трека для заказа:', orderId)
          this.selectedOrderTrack = cached.track
          this.trackStats = cached.stats
          return
        }
      }

      this.isLoadingTrack = true
      try {
        // Загружаем трек из API
        const response = await this.axios.get(
          `/api/v1/taxi/orders/${orderId}/track`,
          {
            params: {
              yandex_integration: {
                order_id: orderId,
                park_id: this.client.company_id // или откуда берется park_id
              }
            },
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success && response.data.data.track) {
          const track = response.data.data.track

          // Рассчитываем статистику по треку
          const stats = this.calculateTrackStats(track)

          // Сохраняем в кэш
          this.orderTracksCache[orderId] = {
            track: track,
            stats: stats,
            loadedAt: Date.now()
          }

          // Обновляем UI
          this.selectedOrderTrack = track
          this.trackStats = stats

          this.$notify('Успех', 'Маршрут поездки загружен', 'success', 3000)
        } else {
          this.$notify('Предупреждение', 'Маршрут для этой поездки недоступен', 'warning', 3000)
        }
      } catch (error) {
        console.error('Error loading order track:', error)
        this.$notify('Ошибка', 'Не удалось загрузить маршрут поездки', 'error', 5000)
      } finally {
        this.isLoadingTrack = false
      }
    },

    /**
     * Расчет статистики по треку заказа
     *
     * Анализирует массив точек трека и вычисляет:
     * - Общее расстояние
     * - Максимальную и среднюю скорость
     * - Время на каждом этапе (в пути, ожидание, перевозка)
     *
     * @param {Array} track - Массив точек трека из API Yandex
     * @returns {Object} Статистика по треку
     */
    calculateTrackStats(track) {
      if (!track || track.length === 0) {
        return {
          totalDistance: 0,
          maxSpeed: 0,
          avgSpeed: 0,
          waitingTime: 0,
          drivingTime: 0,
          transportingTime: 0
        }
      }

      // Общее расстояние - берем из последней точки
      const totalDistance = track[track.length - 1]?.distance || 0

      // Максимальная и средняя скорость
      const speeds = track.map(point => point.speed || 0).filter(s => s > 0)
      const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : 0
      // Конвертируем из м/с в км/ч
      const maxSpeedKmh = Math.round(maxSpeed * 3.6)
      const avgSpeedKmh = speeds.length > 0
        ? Math.round((speeds.reduce((a, b) => a + b, 0) / speeds.length) * 3.6)
        : 0

      // Время на каждом этапе (в минутах)
      const phases = {
        driving: [],
        waiting: [],
        transporting: []
      }

      // Группируем точки по статусам
      track.forEach(point => {
        if (point.order_status && point.tracked_at) {
          phases[point.order_status]?.push(point.tracked_at)
        }
      })

      // Рассчитываем продолжительность каждого этапа
      const calculatePhaseDuration = (timestamps) => {
        if (timestamps.length < 2) return 0
        const start = moment(timestamps[0])
        const end = moment(timestamps[timestamps.length - 1])
        return end.diff(start, 'minutes')
      }

      return {
        totalDistance: totalDistance,
        maxSpeed: maxSpeedKmh,
        avgSpeed: avgSpeedKmh,
        waitingTime: calculatePhaseDuration(phases.waiting),
        drivingTime: calculatePhaseDuration(phases.driving),
        transportingTime: calculatePhaseDuration(phases.transporting)
      }
    },

    // Загрузка транзакций заказа
    async loadOrderTransactions(orderId) {
      this.isLoadingTransactions = true
      try {
        const response = await this.axios.get(
          `/api/v1/taxi/orders/${orderId}/transactions`,
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken
            }
          }
        )

        if (response.data.success) {
          this.orderTransactions = response.data.data.transactions || []
        } else {
          this.$notify('Ошибка', response.data.error || 'Не удалось загрузить транзакции', 'error', 5000)
        }
      } catch (error) {
        console.error('Error loading order transactions:', error)
        this.$notify('Ошибка', 'Ошибка загрузки транзакций', 'error', 5000)
      } finally {
        this.isLoadingTransactions = false
      }
    },

    // Форматирование
    formatDateTime(dateTime) {
      return moment(dateTime).format('DD.MM.YYYY HH:mm')
    },

    /**
     * Форматирование цены с поддержкой больших чисел
     *
     * @param {number|string} price - Цена для форматирования
     * @returns {string} Отформатированная цена с валютой
     */
    formatPrice(price) {
      if (!price) return '0'
      const amount = parseFloat(price)

      // Форматирование больших чисел
      if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(1)}М`
      } else if (amount >= 1000) {
        return `${(amount / 1000).toFixed(1)}К`
      } else {
        return `${amount.toFixed(2)}`
      }
    },

    /**
     * Форматирование больших чисел без валюты
     *
     * @param {number|string} value - Числовое значение
     * @returns {string} Отформатированная строка
     */
    formatNumber(value) {
      if (!value) return '0'
      const num = parseFloat(value)

      // Форматирование больших чисел
      if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}М`
      } else if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}К`
      } else {
        return num.toLocaleString()
      }
    },

    getStatusColor(status) {
      const colors = {
        'complete': 'success',
        'cancelled': 'error',
        'driving': 'info',
        'waiting': 'warning',
        'transporting': 'primary',
        'none': 'grey'
      }
      return colors[status] || 'grey'
    },

    getStatusText(status) {
      const texts = {
        'complete': 'Завершен',
        'cancelled': 'Отменен',
        'driving': 'В пути',
        'waiting': 'Ожидание',
        'transporting': 'Перевозка',
        'none': 'Без статуса'
      }
      return texts[status] || status
    },

    getCategoryColor(category) {
      const colors = {
        'econom': 'blue',
        'comfort': 'green',
        'business': 'purple',
        'vip': 'orange',
        'minivan': 'teal'
      }
      return colors[category] || 'grey'
    },

    getCategoryText(category) {
      const texts = {
        'econom': 'Эконом',
        'comfort': 'Комфорт',
        'business': 'Бизнес',
        'vip': 'VIP',
        'minivan': 'Минивен'
      }
      return texts[category] || category
    },

    toKilometers(distance) {
      if (!distance) return '0 км'
      return `${(distance / 1000).toFixed(1)} км`
    },

    /**
     * Форматирование продолжительности из минут в удобочитаемый вид
     *
     * Преобразует количество минут в формат "Xч Yм" или "Yм" для коротких периодов.
     *
     * Примеры:
     * - 0 минут -> "0 мин"
     * - 30 минут -> "30м"
     * - 90 минут -> "1ч 30м"
     * - 120 минут -> "2ч"
     *
     * @param {number} minutes - Количество минут
     * @returns {string} Отформатированная строка времени
     */
    formatDuration(minutes) {
      // Если время не указано или равно 0, возвращаем "0 мин"
      if (!minutes || minutes === 0) return '0 мин'

      // Вычисляем часы и оставшиеся минуты
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60

      // Форматируем результат в зависимости от наличия часов
      if (hours > 0) {
        // Если есть и часы, и минуты, показываем оба
        // Если есть только часы (минуты = 0), показываем только часы
        return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`
      } else {
        // Если часов нет, показываем только минуты
        return `${mins}м`
      }
    },

    getPaymentMethodText(method) {
      const texts = {
        'cashless': 'Безналичный',
        'cash': 'Наличные',
        'card': 'Карта'
      }
      return texts[method] || method
    },

    getCancellationReasonText(reason) {
      const texts = {
        'user': 'Отмена пользователем',
        'driver': 'Отмена водителем',
        'system': 'Отмена системой'
      }
      return texts[reason] || reason
    }
  }
}
</script>

<style scoped>
.taxi-driver-info {
  padding: 16px;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Стили для карточек аналитики */
.analytics-card {
  transition: all 0.3s ease;
  border-radius: 12px !important;
  min-height: 140px;
}

.analytics-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Улучшенные цвета для темной темы */
.theme--dark .analytics-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Улучшенные цвета для светлой темы */
.theme--light .analytics-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%) !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

/* Анимация для чисел */
.text-h3 {
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.5px;
}

/* Улучшенные иконки */
.analytics-card .v-icon {
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.analytics-card:hover .v-icon {
  opacity: 1;
}
</style>
